import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Dropdown,
  Empty,
  Input,
  InputNumber,
  Menu,
  Message,
  Modal,
  Pagination,
  Popconfirm,
  Popover,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowUp,
  Columns3,
  FilePlus2,
  History,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Save,
  Search,
  Trash2,
} from "lucide-react";
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import {
  CustomizationNewProjectTraceDraft,
  CustomizationProjectListFeaturePage,
  buildCustomizationWorkbenchEntryPath,
  buildCustomizationWorkbenchFallbackPath,
  buildOpportunityMobileCustomizationEntrySearch,
  isCustomizationMobileDevice,
  openCustomizationWorkbenchInNewWindow,
  OPPORTUNITY_OPEN_ADD_CUSTOMIZATION_QUERY_KEY,
  OPPORTUNITY_OPEN_CUSTOMIZATION_QUERY_KEY,
} from "../../customization";
import { CustomizationItineraryPreviewHost } from "../../customization/ui/CustomizationItineraryPreviewHost";
import { createOrderHandoverDraftOrder } from "../../order-handover";
import { canUsePermission } from "../../../lib/auth";
import { FORBIDDEN_USER_MESSAGE, isHttpClientError } from "../../../lib/http/errors";
import { SearchHistoryInput } from "../../../shared/ui/SearchHistoryInput";
import {
  WorkbenchColumnSettings,
  loadWorkbenchVisibleColumnsPreference,
  normalizeWorkbenchVisibleColumns,
  saveWorkbenchVisibleColumnsPreference,
} from "../../../shared/ui/WorkbenchColumnSettings";
import {
  rememberSearchHistory,
  SEARCH_HISTORY_KEYS,
} from "../../../lib/searchHistoryStorage";
import {
  crmOpportunityWorkbenchButtonPermissions,
  opportunitySelectionStatusLabels,
} from "../config";
import {
  CRM_OPPORTUNITY_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
  crmOpportunityWorkbenchColumnOptions,
  crmOpportunityWorkbenchDefaultVisibleColumns,
} from "../config/columns";
import {
  CRM_OPPORTUNITY_PROGRESS_CHOICES,
  CRM_OPPORTUNITY_SIGN_BUSI_SOURCE_OPTIONS,
  CRM_OPPORTUNITY_SIGN_INFO_STATUS_FILTER_OPTIONS,
  CRM_OPPORTUNITY_SIGN_LOSE_REASON_OPTIONS,
  CRM_OPPORTUNITY_SIGN_WIN_REASON_OPTIONS,
} from "../config/signInfoOptions";
import {
  formatOpportunityListDateTime,
  OPPORTUNITY_LEGACY_STAGE_LABELS,
} from "../service";
import { useCrmOpportunityWorkbenchRuntime, OPPORTUNITY_WORKBENCH_MOBILE_PAGE_SIZE } from "../runtime/useCrmOpportunityWorkbenchRuntime";
import {
  disableOpportunity,
  fetchOpportunityCustomizationCandidates,
  reassignOpportunityOwners,
  reassignOpportunityCustomizations,
} from "../repo/crmOpportunityWorkbenchRepo";
import {
  deleteCustomizationDemand,
  deleteCustomizationProject,
} from "../../customization/repo/customizationRepo";
import { CrmOpportunitySplitFeaturePage } from "./CrmOpportunitySplitFeaturePage";
import type {
  OpportunityContactOption,
  OpportunityCustomerOption,
  OpportunityCustomizationCandidate,
  OpportunityFollowupRecord,
  OpportunityWorkbenchColumnKey,
  OpportunityOrganizationOption,
  OpportunityProgress,
  OpportunitySignInfoChoice,
  OpportunitySignInfoDetail,
  OpportunitySignInfoSavePayload,
  OpportunityStageOwnerItem,
  OpportunityStageBoard,
  OpportunityWorkbenchItem,
} from "../types";
import "./CrmOpportunityWorkbenchPage.css";
import "../../../shared/ui/crmListPageLayout.css";
import "../../../shared/ui/adminEditorForm.css";

/** 状态下拉「全部」选项值；避免空字符串导致 Arco Select 无法展开。 */
const OPPORTUNITY_STATUS_FILTER_ALL = "__all__";
/** 内容区下滑超过该距离后显示回到顶部。 */
const OPPORTUNITY_BACK_TO_TOP_SCROLL_THRESHOLD = 240;

/** CRM 商机工作台主页面。 */
export function CrmOpportunityWorkbenchFeaturePage() {
  const runtime = useCrmOpportunityWorkbenchRuntime();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentAuthUser().data;
  const isMobileViewport = useIsMobileViewport();
  const pageRef = useRef<HTMLElement | null>(null);
  const tableShellRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingScrollRef = useRef(false);
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const canCreate = canUsePermission(currentUser, crmOpportunityWorkbenchButtonPermissions.page);
  const canUpdate = canUsePermission(currentUser, crmOpportunityWorkbenchButtonPermissions.page);
  const canCreateCustomization = canUsePermission(
    currentUser,
    crmOpportunityWorkbenchButtonPermissions.createCustomization,
  );
  const canFindCustomization = canUsePermission(
    currentUser,
    crmOpportunityWorkbenchButtonPermissions.findCustomization,
  );
  const canSplitOpportunity = canUsePermission(
    currentUser,
    crmOpportunityWorkbenchButtonPermissions.splitOpportunity,
  );
  const canManageOrderHandover = canUsePermission(
    currentUser,
    crmOpportunityWorkbenchButtonPermissions.orderHandover,
  );
  const canAccessOrderHandover = canUsePermission(
    currentUser,
    crmOpportunityWorkbenchButtonPermissions.orderHandoverAccess,
  );
  /** 创订单需同时具备合同交接访问与管理权限，避免进页后被路由或写接口拒绝。 */
  const canEnterOrderHandover = canAccessOrderHandover && canManageOrderHandover;
  const stageLabelMap = useMemo(() => {
    const map = buildLabelMap(runtime.metadata?.stageOptions);
    for (const [code, label] of Object.entries(OPPORTUNITY_LEGACY_STAGE_LABELS)) {
      if (!map.has(code)) {
        map.set(code, label);
      }
    }
    return map;
  }, [runtime.metadata?.stageOptions]);
  const statusLabelMap = useMemo(
    () => buildLabelMap(runtime.metadata?.statusOptions),
    [runtime.metadata?.statusOptions],
  );
  const stageGroupLabelMap = useMemo(
    () => buildLabelMap(runtime.metadata?.stageGroupOptions),
    [runtime.metadata?.stageGroupOptions],
  );
  const [filterOrganizationOptions, setFilterOrganizationOptions] = useState<
    OpportunityOrganizationOption[]
  >(() => {
    const orgId = runtime.draftFilters.orgId;
    const orgName = runtime.draftFilters.orgName?.trim();
    return orgId
      ? [{ id: orgId, orgName: orgName || String(orgId) }]
      : [];
  });
  const [filterOrganizationLoading, setFilterOrganizationLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<OpportunityWorkbenchColumnKey[]>(() =>
    loadWorkbenchVisibleColumnsPreference(
      CRM_OPPORTUNITY_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
      crmOpportunityWorkbenchColumnOptions,
      crmOpportunityWorkbenchDefaultVisibleColumns,
    ),
  );
  const [selectedCustomizationOpportunity, setSelectedCustomizationOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [addCustomizationOpportunity, setAddCustomizationOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [transferringOrderOpportunityId, setTransferringOrderOpportunityId] = useState<
    number | undefined
  >();
  const [findCustomizationOpportunity, setFindCustomizationOpportunity] =
    useState<OpportunityWorkbenchItem | null>(null);
  const [findCustomizationCandidates, setFindCustomizationCandidates] = useState<
    OpportunityCustomizationCandidate[]
  >([]);
  const [findCustomizationLoading, setFindCustomizationLoading] = useState(false);
  const [reassigningCustomizations, setReassigningCustomizations] = useState(false);
  const [selectedCandidateProjectIds, setSelectedCandidateProjectIds] = useState<string[]>([]);
  const [customizationDrawerVersion, setCustomizationDrawerVersion] = useState(0);
  const [splitOpportunityId, setSplitOpportunityId] = useState<number | null>(null);
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<number[]>([]);
  const [reassignOwnerUserId, setReassignOwnerUserId] = useState<number | undefined>();
  const [reassigningOwners, setReassigningOwners] = useState(false);
  const [reassignConfirmVisible, setReassignConfirmVisible] = useState(false);
  const [previewItineraryId, setPreviewItineraryId] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as { openSplitOpportunityId?: number } | null;
    const incomingId = state?.openSplitOpportunityId;
    if (incomingId == null || !Number.isFinite(incomingId)) {
      return;
    }
    setSplitOpportunityId(incomingId);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  /** 从手机定制返回时，按入口分别重新打开关联定制或增定制抽屉。 */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkedId = params.get(OPPORTUNITY_OPEN_CUSTOMIZATION_QUERY_KEY);
    const addId = params.get(OPPORTUNITY_OPEN_ADD_CUSTOMIZATION_QUERY_KEY);
    const rawId = linkedId ?? addId;
    if (!rawId) {
      return;
    }
    const restoreAdd = addId != null;
    const opportunityId = Number(rawId);
    const stripRestoreQuery = () => {
      let changed = false;
      if (params.has(OPPORTUNITY_OPEN_CUSTOMIZATION_QUERY_KEY)) {
        params.delete(OPPORTUNITY_OPEN_CUSTOMIZATION_QUERY_KEY);
        changed = true;
      }
      if (params.has(OPPORTUNITY_OPEN_ADD_CUSTOMIZATION_QUERY_KEY)) {
        params.delete(OPPORTUNITY_OPEN_ADD_CUSTOMIZATION_QUERY_KEY);
        changed = true;
      }
      if (!changed) {
        return;
      }
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, {
        replace: true,
        state: location.state,
      });
    };
    if (!Number.isFinite(opportunityId) || opportunityId <= 0) {
      stripRestoreQuery();
      return;
    }
    if (runtime.loading) {
      return;
    }
    const item = runtime.items.find((row) => row.id === opportunityId);
    if (item) {
      if (restoreAdd) {
        setAddCustomizationOpportunity(item);
        setSelectedCustomizationOpportunity(null);
      } else {
        setSelectedCustomizationOpportunity(item);
        setAddCustomizationOpportunity(null);
      }
    }
    stripRestoreQuery();
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    runtime.items,
    runtime.loading,
  ]);

  /** 窄屏默认每页 5 条，避免卡片列表一次加载过多。 */
  useEffect(() => {
    if (!isMobileViewport) {
      return;
    }
    if (runtime.pageSize === OPPORTUNITY_WORKBENCH_MOBILE_PAGE_SIZE) {
      return;
    }
    runtime.handlePageSizeChange(OPPORTUNITY_WORKBENCH_MOBILE_PAGE_SIZE);
  }, [isMobileViewport, runtime.pageSize]);

  /** 搜索列表筛选区的学校候选。 */
  async function handleSearchFilterOrganizations(keyword: string) {
    setFilterOrganizationLoading(true);
    try {
      setFilterOrganizationOptions(await runtime.searchOrganizations(keyword));
    } finally {
      setFilterOrganizationLoading(false);
    }
  }

  /** 记住文本搜索条件并查询商机列表。 */
  function handleOpportunityFilterSearch() {
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.opportunityKeyword,
      runtime.draftFilters.keyword ?? "",
    );
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.opportunityCustomizationKeyword,
      runtime.draftFilters.customizationKeyword ?? "",
    );
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.opportunityCustomerKeyword,
      runtime.draftFilters.customerKeyword ?? "",
    );
    runtime.handleSearch();
  }

  /** 对指定定制项目执行创订单/查订单并进入合同交接。 */
  async function transferProjectToOrder(
    opportunity: OpportunityWorkbenchItem,
    project: { projectId: string; projectName?: string },
  ) {
    if (!canEnterOrderHandover) {
      Message.warning(FORBIDDEN_USER_MESSAGE);
      return;
    }
    if (transferringOrderOpportunityId) {
      return;
    }
    setTransferringOrderOpportunityId(opportunity.id);
    try {
      await createOrderHandoverDraftOrder(project.projectId);
      const params = new URLSearchParams();
      params.set("from", "opportunity");
      if (project.projectName) {
        params.set("projectName", project.projectName);
      }
      setSelectedCustomizationOpportunity(null);
      navigate(`/crm/order-handover/${project.projectId}?${params.toString()}`);
    } catch (error) {
      // 无权限时全局拦截器已提示；此处只保证不跳转。
      if (isHttpClientError(error) && error.statusCode === 403) {
        return;
      }
      Message.error("生成订单失败，请稍后重试；当前仍保留在商机列表。");
    } finally {
      setTransferringOrderOpportunityId(undefined);
    }
  }

  /** 打开找定制抽屉并加载候选项目。 */
  async function openFindCustomization(item: OpportunityWorkbenchItem) {
    setFindCustomizationOpportunity(item);
    setSelectedCandidateProjectIds([]);
    setFindCustomizationLoading(true);
    try {
      const result = await fetchOpportunityCustomizationCandidates(item.id);
      setFindCustomizationCandidates(result.items);
    } catch {
      Message.error("读取可归类定制失败，请稍后重试。");
      setFindCustomizationCandidates([]);
    } finally {
      setFindCustomizationLoading(false);
    }
  }

  /** 将勾选的候选定制重新归类到当前商机。 */
  async function reassignSelectedCustomizations() {
    if (
      !findCustomizationOpportunity ||
      selectedCandidateProjectIds.length === 0 ||
      reassigningCustomizations
    ) {
      return;
    }
    setReassigningCustomizations(true);
    try {
      const result = await reassignOpportunityCustomizations({
        opportunityId: findCustomizationOpportunity.id,
        projectIds: selectedCandidateProjectIds,
        expectedProjectUpdatedAt: Object.fromEntries(
          findCustomizationCandidates
            .filter((candidate) => selectedCandidateProjectIds.includes(candidate.projectId))
            .map((candidate) => [candidate.projectId, candidate.projectUpdatedAt]),
        ),
      });
      if (result.legacyIdentityGovernanceStatus === "completed") {
        Message.success("定制已归类到当前商机，旧系统归组已同步。");
      } else if (result.legacyIdentityGovernanceStatus === "recovery_required") {
        Message.warning("定制已归类到当前商机；旧系统归组未完成，已保留恢复记录。");
      } else {
        Message.warning("定制已归类到当前商机；旧身份仍需治理，旧系统归组待处理。");
      }
      setFindCustomizationOpportunity(null);
      setSelectedCandidateProjectIds([]);
      setCustomizationDrawerVersion((value) => value + 1);
      await runtime.refreshList();
    } catch {
      Message.error("定制重新归类失败，请刷新候选后重试。");
    } finally {
      setReassigningCustomizations(false);
    }
  }

  /** 打开商机负责人转派确认弹窗。 */
  function confirmReassignSelectedOwners() {
    const selectedItems = runtime.items.filter((item) => selectedOpportunityIds.includes(item.id));
    const owner = (runtime.metadata?.staffOptions ?? []).find(
      (option) => option.id === reassignOwnerUserId,
    );
    if (
      selectedItems.length === 0 ||
      !owner ||
      owner.disabled ||
      selectedItems.some((item) => item.accessPolicy?.canAssignOwner !== true) ||
      reassigningOwners
    ) {
      return;
    }
    setReassignConfirmVisible(true);
  }

  /** 执行确认后的商机负责人转派。 */
  async function executeReassignSelectedOwners() {
    const selectedItems = runtime.items.filter((item) => selectedOpportunityIds.includes(item.id));
    const owner = (runtime.metadata?.staffOptions ?? []).find(
      (option) => option.id === reassignOwnerUserId,
    );
    if (
      selectedItems.length === 0 ||
      !owner ||
      owner.disabled ||
      selectedItems.some((item) => item.accessPolicy?.canAssignOwner !== true) ||
      reassigningOwners
    ) {
      return;
    }
    setReassigningOwners(true);
    try {
      await reassignOpportunityOwners({
        opportunityIds: selectedOpportunityIds,
        ownerUserId: owner.id,
      });
      Message.success(`已转派 ${selectedItems.length} 条商机`);
      setSelectedOpportunityIds([]);
      setReassignOwnerUserId(undefined);
      setReassignConfirmVisible(false);
      await runtime.refreshList();
    } catch (error) {
      Message.error(error instanceof Error ? error.message : "商机转派失败，请稍后重试");
    } finally {
      setReassigningOwners(false);
    }
  }

  /** 删除关联定制项目；后端根据下级业务关联决定物理或逻辑删除。 */
  async function deleteAssociatedCustomizationProject(projectId: string) {
    try {
      const result = await deleteCustomizationProject(projectId);
      const message =
        result.mode === "physical"
          ? "定制已物理删除"
          : "定制已逻辑归档（保留业务历史）";
      if (result.legacySyncStatus === "partial_failed") {
        Message.warning(`${message}；旧库无对应记录，不影响本次删除`);
      } else {
        Message.success(message);
      }
      setCustomizationDrawerVersion((value) => value + 1);
      await runtime.refreshList();
    } catch {
      Message.error("定制删除失败；若旧库存在但停用回写失败，请稍后重试。");
    }
  }

  /** 删除关联定制项目中的单个需求版本；V1 改为删整项目并停用商机。 */
  async function deleteAssociatedCustomizationDemand(
    projectId: string,
    demandId: string,
    options?: { isRootVersion: boolean },
  ) {
    const opportunityId = selectedCustomizationOpportunity?.id;
    try {
      if (options?.isRootVersion) {
        if (!opportunityId) {
          Message.error("缺少商机上下文，无法删除 V1");
          return;
        }
        // 先删定制，再停用商机，避免先停用后触发定制写门禁。
        const result = await deleteCustomizationProject(projectId);
        await disableOpportunity(opportunityId);
        const deleteMessage =
          result.mode === "physical"
            ? "定制已物理删除"
            : "定制已逻辑归档（保留业务历史）";
        if (result.legacySyncStatus === "partial_failed") {
          Message.warning(`${deleteMessage}，并已停用商机；旧库无对应记录，不影响本次删除`);
        } else {
          Message.success(`${deleteMessage}，并已停用商机`);
        }
        setSelectedCustomizationOpportunity(null);
        setCustomizationDrawerVersion((value) => value + 1);
        await runtime.refreshList();
        return;
      }
      const result = await deleteCustomizationDemand({ projectId, demandId });
      Message.success(
        result.mode === "physical"
          ? "需求版本已物理删除"
          : "需求版本已逻辑归档（保留业务历史）",
      );
      setCustomizationDrawerVersion((value) => value + 1);
    } catch {
      Message.error(
        options?.isRootVersion
          ? "删除 V1 并停用商机失败；若旧库停用或下级业务检查失败，请稍后重试。"
          : "需求版本删除失败；若旧库停用或下级业务检查失败，请稍后重试。",
      );
    }
  }

  const handleVisibleColumnsChange = useCallback((columns: OpportunityWorkbenchColumnKey[]) => {
    const normalizedColumns = normalizeWorkbenchVisibleColumns(
      columns,
      crmOpportunityWorkbenchColumnOptions,
      crmOpportunityWorkbenchDefaultVisibleColumns,
    );
    setVisibleColumns(normalizedColumns);
    saveWorkbenchVisibleColumnsPreference(
      CRM_OPPORTUNITY_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
      normalizedColumns,
    );
  }, []);

  const columns = useMemo(
    () =>
      buildOpportunityColumns({
        visibleColumns,
        canUpdate,
        stageLabelMap,
        statusLabelMap,
        onOpenStage: runtime.openStageDrawer,
        sortField: runtime.sortField,
        sortDirection: runtime.sortDirection,
        onSort: runtime.handleSort,
        onOpenFollowup: runtime.openFollowupDrawer,
        onOpenSignInfo: runtime.openSignInfoDrawer,
        onEdit: runtime.openEditDrawer,
        onDisable: (item) => void runtime.disableOpportunity(item.id),
        onEnable: (item) => void runtime.enableOpportunity(item.id),
        canCreateCustomization,
        canFindCustomization,
        canSplitOpportunity,
        onOpenCustomizations: setSelectedCustomizationOpportunity,
        onOpenLatestItinerary: (item) => {
          const itineraryId = item.latestItineraryId?.trim();
          if (!itineraryId) {
            return;
          }
          setPreviewItineraryId(itineraryId);
        },
        onAddCustomization: setAddCustomizationOpportunity,
        onSplit: (item) => setSplitOpportunityId(item.id),
        onFindCustomizations: (item) => void openFindCustomization(item),
        onOpenStageOwners: runtime.openStageOwnerDrawer,
      }),
    [
      canCreateCustomization,
      canFindCustomization,
      canSplitOpportunity,
      canUpdate,
      navigate,
      runtime,
      stageLabelMap,
      statusLabelMap,
      visibleColumns,
    ],
  );

  const tableScrollWidth = columns.reduce(
    (total, column) => total + (typeof column.width === "number" ? column.width : 0),
    0,
  );

  const findTableScroller = useCallback(() => {
    const tableShell = tableShellRef.current;
    // Arco 仅 scroll.x 时，横向滚动发生在 content-inner；同时设 y 时才在 table-body。
    return (
      tableShell?.querySelector<HTMLElement>(".arco-table-content-inner") ??
      tableShell?.querySelector<HTMLElement>(".arco-table-body") ??
      tableShell?.querySelector<HTMLElement>(".arco-table-content-scroll") ??
      null
    );
  }, []);

  const syncHorizontalScrollFrom = useCallback(
    (source: HTMLElement) => {
      if (syncingScrollRef.current) {
        return;
      }
      const peers = [
        topScrollRef.current,
        bottomScrollRef.current,
        findTableScroller(),
      ].filter((node): node is HTMLElement => Boolean(node) && node !== source);
      if (peers.length === 0) {
        return;
      }
      syncingScrollRef.current = true;
      const nextLeft = source.scrollLeft;
      for (const peer of peers) {
        if (peer.scrollLeft !== nextLeft) {
          peer.scrollLeft = nextLeft;
        }
      }
      window.requestAnimationFrame(() => {
        syncingScrollRef.current = false;
      });
    },
    [findTableScroller],
  );

  const handleTopScroll = useCallback(() => {
    const topScroll = topScrollRef.current;
    if (!topScroll) {
      return;
    }
    syncHorizontalScrollFrom(topScroll);
  }, [syncHorizontalScrollFrom]);

  const handleBottomScroll = useCallback(() => {
    const bottomScroll = bottomScrollRef.current;
    if (!bottomScroll) {
      return;
    }
    syncHorizontalScrollFrom(bottomScroll);
  }, [syncHorizontalScrollFrom]);

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const bottomScroll = bottomScrollRef.current;
    const tableScroller = findTableScroller();
    if (!topScroll || !bottomScroll || !tableScroller) {
      return undefined;
    }

    const updateMetrics = () => {
      setTopScrollWidth(tableScroller.scrollWidth);
      const nextLeft = tableScroller.scrollLeft;
      if (topScroll.scrollLeft !== nextLeft) {
        topScroll.scrollLeft = nextLeft;
      }
      if (bottomScroll.scrollLeft !== nextLeft) {
        bottomScroll.scrollLeft = nextLeft;
      }
    };
    const handleTableScroll = () => syncHorizontalScrollFrom(tableScroller);
    tableScroller.addEventListener("scroll", handleTableScroll);
    window.addEventListener("resize", updateMetrics);
    updateMetrics();

    const observer =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateMetrics);
    observer?.observe(tableScroller);
    if (tableScroller.firstElementChild instanceof HTMLElement) {
      observer?.observe(tableScroller.firstElementChild);
    }

    return () => {
      tableScroller.removeEventListener("scroll", handleTableScroll);
      window.removeEventListener("resize", updateMetrics);
      observer?.disconnect();
    };
  }, [columns, findTableScroller, runtime.items, syncHorizontalScrollFrom]);

  useEffect(() => {
    const page = pageRef.current;
    const scrollContainer =
      page?.closest<HTMLElement>("[data-testid='admin-content-shell']") ??
      page?.closest<HTMLElement>(".arco-layout-content");
    if (!scrollContainer) {
      setShowBackToTop(false);
      return undefined;
    }
    const updateVisibility = () => {
      setShowBackToTop(scrollContainer.scrollTop > OPPORTUNITY_BACK_TO_TOP_SCROLL_THRESHOLD);
    };
    updateVisibility();
    scrollContainer.addEventListener("scroll", updateVisibility, { passive: true });
    return () => {
      scrollContainer.removeEventListener("scroll", updateVisibility);
    };
  }, [runtime.items.length, runtime.page]);

  /** 回到列表顶部。 */
  function handleBackToTop() {
    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    pageRef.current?.scrollIntoView?.({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }

  return (
    <main className="crm-opportunity-workbench-page" ref={pageRef}>
      {isMobileViewport ? (
        <>
          <MobileOpportunityFilterBar
            runtime={runtime}
            canCreate={canCreate}
            onCreate={runtime.openCreateDrawer}
            organizationOptions={filterOrganizationOptions}
            organizationLoading={filterOrganizationLoading}
            onSearchOrganizations={handleSearchFilterOrganizations}
          />
          <MobileOpportunitySortBar
            sortField={runtime.sortField}
            sortDirection={runtime.sortDirection}
            onSort={runtime.handleSort}
          />
        </>
      ) : (
      <section
        className="crm-opportunity-workbench-filter-panel"
        role="search"
        aria-label="商机筛选"
        data-layout="two-tier-responsive"
      >
        <div
          className="crm-opportunity-workbench-filter-fields"
          data-testid="opportunity-filter-fields"
        >
          <div className="crm-list-filter-primary crm-opportunity-workbench-filter-primary">
            <label className="crm-opportunity-workbench-field crm-opportunity-workbench-keyword-field">
              <span>商机关键词</span>
              <SearchHistoryInput
                storageKey={SEARCH_HISTORY_KEYS.opportunityKeyword}
                placeholder="模糊匹配商机号、商机名称"
                value={runtime.draftFilters.keyword ?? ""}
                onChange={(keyword) =>
                  runtime.setDraftFilters((current) => ({ ...current, keyword }))
                }
                onPressEnter={handleOpportunityFilterSearch}
              />
            </label>
            <label className="crm-opportunity-workbench-field crm-opportunity-workbench-keyword-field">
              <span>定制关键词</span>
              <SearchHistoryInput
                storageKey={SEARCH_HISTORY_KEYS.opportunityCustomizationKeyword}
                placeholder="模糊匹配定制号、定制名称"
                value={runtime.draftFilters.customizationKeyword ?? ""}
                onChange={(customizationKeyword) =>
                  runtime.setDraftFilters((current) => ({ ...current, customizationKeyword }))
                }
                onPressEnter={handleOpportunityFilterSearch}
              />
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>学校</span>
              <Select
                allowClear
                showSearch
                data-testid="opportunity-filter-school"
                filterOption={false}
                loading={filterOrganizationLoading}
                dropdownMenuClassName="crm-opportunity-workbench-option-dropdown crm-opportunity-workbench-filter-dropdown"
                dropdownMenuStyle={{ minWidth: 360 }}
                triggerProps={{
                  autoAlignPopupWidth: false,
                  autoFitPosition: true,
                  boundaryDistance: { right: 8, bottom: 8 },
                  position: "br",
                }}
                placeholder="搜索学校"
                value={runtime.draftFilters.orgId}
                onFocus={() => void handleSearchFilterOrganizations("")}
                onSearch={(keyword) => void handleSearchFilterOrganizations(keyword)}
                onChange={(value) => {
                  const orgId = parseOptionalSelectNumber(value);
                  const organization = filterOrganizationOptions.find((option) => option.id === orgId);
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    orgId,
                    orgName: organization?.orgName,
                  }));
                }}
              >
                {filterOrganizationOptions.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    <OptionLabel title={formatOrganizationOption(option)} />
                  </Select.Option>
                ))}
              </Select>
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>客户名</span>
              <SearchHistoryInput
                storageKey={SEARCH_HISTORY_KEYS.opportunityCustomerKeyword}
                placeholder="模糊匹配客户名"
                value={runtime.draftFilters.customerKeyword ?? ""}
                onChange={(customerKeyword) =>
                  runtime.setDraftFilters((current) => ({ ...current, customerKeyword }))
                }
                onPressEnter={handleOpportunityFilterSearch}
              />
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>出行月份</span>
              <div
                onChangeCapture={(event) => {
                  const target = event.target as HTMLInputElement;
                  const travelMonth = normalizeTravelMonthInput(target.value);
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    travelMonth,
                  }));
                }}
              >
                <DatePicker.MonthPicker
                  allowClear
                  format="YYYY-MM"
                  placeholder="选择月份"
                  value={runtime.draftFilters.travelMonth}
                  onChange={(value) =>
                    runtime.setDraftFilters((current) => ({
                      ...current,
                      travelMonth: typeof value === "string" && value ? value : undefined,
                    }))
                  }
                />
              </div>
            </label>
            <div className="crm-opportunity-workbench-field crm-opportunity-workbench-status-field">
              <span>状态</span>
              <Select
                aria-label="状态筛选"
                data-testid="opportunity-filter-status"
                placeholder="全部"
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                value={runtime.draftFilters.status ?? OPPORTUNITY_STATUS_FILTER_ALL}
                onChange={(status) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    status:
                      !status || status === OPPORTUNITY_STATUS_FILTER_ALL
                        ? undefined
                        : String(status),
                  }))
                }
              >
                <Select.Option value={OPPORTUNITY_STATUS_FILTER_ALL}>全部</Select.Option>
                {(runtime.metadata?.statusOptions ?? []).map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="crm-opportunity-workbench-field crm-opportunity-workbench-status-field">
              <span>商机进度</span>
              <Select
                allowClear
                mode="multiple"
                maxTagCount={1}
                aria-label="商机进度筛选"
                data-testid="opportunity-filter-sign-info-status"
                className="crm-opportunity-workbench-compact-multi-select"
                placeholder="可多选"
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={runtime.draftFilters.signInfoStatuses ?? []}
                onChange={(signInfoStatuses) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    signInfoStatuses: Array.isArray(signInfoStatuses)
                      ? signInfoStatuses
                          .map(String)
                          .filter(
                            (value): value is "unfilled" | "won" | "lost" =>
                              value === "unfilled" || value === "won" || value === "lost",
                          )
                      : [],
                  }))
                }
              >
                {CRM_OPPORTUNITY_SIGN_INFO_STATUS_FILTER_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <label className="crm-opportunity-workbench-field crm-opportunity-workbench-stages-field">
              <span>当前阶段</span>
              <Select
                allowClear
                mode="multiple"
                maxTagCount={1}
                className="crm-opportunity-workbench-compact-multi-select"
                dropdownMenuClassName="crm-opportunity-workbench-filter-dropdown"
                data-testid="opportunity-filter-stages"
                placeholder="可多选阶段"
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={runtime.draftFilters.stages ?? []}
                onChange={(stages) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    stage: undefined,
                    stages: Array.isArray(stages) ? stages.map(String) : [],
                  }))
                }
              >
                {(runtime.metadata?.stageOptions ?? []).map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </label>
          </div>
        </div>
        <div
          className="crm-opportunity-workbench-filter-actions"
          data-testid="opportunity-filter-actions"
        >
          <Button icon={<RotateCcw size={16} />} onClick={runtime.handleReset}>
            重置
          </Button>
          <Button type="primary" icon={<Search size={16} />} onClick={handleOpportunityFilterSearch}>
            查询
          </Button>
        </div>
      </section>
      )}

      <section className="crm-opportunity-workbench-table-card">
        {isMobileViewport ? (
          <MobileOpportunityList
            runtime={runtime}
            canUpdate={canUpdate}
            canCreateCustomization={canCreateCustomization}
            onOpenCustomizations={setSelectedCustomizationOpportunity}
            onAddCustomization={setAddCustomizationOpportunity}
            onEdit={runtime.openEditDrawer}
          />
        ) : (
        <>
        <div className="crm-opportunity-workbench-table-header">
          <div className="crm-list-title-row">
            <h2>商机列表</h2>
          </div>
          <div className="crm-list-table-actions">
            <div className="crm-list-statline">共 {runtime.total} 条</div>
            {selectedOpportunityIds.length > 0 ? (
              <div className="crm-opportunity-workbench-reassign-actions">
                <Select
                  showSearch
                  allowClear
                  placeholder="选择新负责人"
                  value={reassignOwnerUserId}
                  data-testid="opportunity-reassign-owner"
                  filterOption={(inputValue, option) =>
                    String((option as ReactElement<{ children?: ReactNode }>).props.children ?? "")
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={(value) =>
                    setReassignOwnerUserId(value == null || value === "" ? undefined : Number(value))
                  }
                  disabled={reassigningOwners}
                  style={{ width: 190 }}
                >
                  {(runtime.metadata?.staffOptions ?? [])
                    .filter((staff) => !staff.disabled)
                    .map((staff) => (
                      <Select.Option key={staff.id} value={staff.id}>
                        {staff.displayName || staff.username}
                      </Select.Option>
                    ))}
                </Select>
                <Button
                  type="primary"
                  data-testid="opportunity-reassign-button"
                  loading={reassigningOwners}
                  disabled={
                    !reassignOwnerUserId ||
                    runtime.items
                      .filter((item) => selectedOpportunityIds.includes(item.id))
                      .some((item) => item.accessPolicy?.canAssignOwner !== true)
                  }
                  onClick={confirmReassignSelectedOwners}
                >
                  转派商机
                </Button>
              </div>
            ) : null}
            {canCreate ? (
              <Button type="primary" icon={<FilePlus2 size={16} />} onClick={runtime.openCreateDrawer}>
                新增商机
              </Button>
            ) : null}
            <Popover
              position="br"
              trigger="click"
              content={
                <WorkbenchColumnSettings
                  options={crmOpportunityWorkbenchColumnOptions}
                  visibleColumns={visibleColumns}
                  onChange={handleVisibleColumnsChange}
                />
              }
            >
              <Button icon={<Columns3 size={14} />}>列设置</Button>
            </Popover>
          </div>
        </div>
        <div
          className="crm-opportunity-workbench-table-shell"
          ref={tableShellRef}
          data-testid="opportunity-table-shell"
          data-table-fit="full"
        >
          <div
            className="crm-opportunity-workbench-h-scroll"
            data-testid="opportunity-workbench-top-scroll"
            ref={topScrollRef}
            aria-label="商机列表顶部横向滚动"
            onScroll={handleTopScroll}
          >
            <div
              className="crm-opportunity-workbench-h-scroll-inner"
              data-testid="opportunity-workbench-top-scroll-inner"
              style={{ width: topScrollWidth || tableScrollWidth }}
            />
          </div>
          <Spin loading={runtime.loading}>
            <Table
              rowKey="id"
              data={runtime.items}
              columns={columns}
              pagination={false}
              tableLayoutFixed
              scroll={{ x: tableScrollWidth }}
              rowSelection={{
                selectedRowKeys: selectedOpportunityIds,
                onChange: (keys) => setSelectedOpportunityIds(keys.map((key) => Number(key))),
                checkboxProps: (item) => ({
                  disabled: item.accessPolicy?.canAssignOwner !== true,
                }),
              }}
            />
          </Spin>
          <div
            className="crm-opportunity-workbench-h-scroll"
            data-testid="opportunity-workbench-bottom-scroll"
            ref={bottomScrollRef}
            aria-label="商机列表底部横向滚动"
            onScroll={handleBottomScroll}
          >
            <div
              className="crm-opportunity-workbench-h-scroll-inner"
              data-testid="opportunity-workbench-bottom-scroll-inner"
              style={{ width: topScrollWidth || tableScrollWidth }}
            />
          </div>
        </div>
        <Pagination
          total={runtime.total}
          current={runtime.page}
          pageSize={runtime.pageSize}
          showTotal
          showJumper
          sizeCanChange
          onChange={runtime.handlePageChange}
          onPageSizeChange={runtime.handlePageSizeChange}
          style={{ marginTop: 12, justifyContent: "flex-end" }}
        />
        </>
        )}
      </section>

      <OpportunityEditorDrawer
        visible={runtime.createDrawerVisible}
        mode="create"
        mutating={runtime.mutating}
        onCancel={runtime.closeCreateDrawer}
        onSave={runtime.createOpportunity}
        onSearchOrganizations={runtime.searchOrganizations}
        onSearchCustomers={runtime.searchCustomers}
        onSearchContacts={runtime.searchContacts}
      />
      <OpportunityEditorDrawer
        visible={Boolean(runtime.selectedEditOpportunity)}
        mode="edit"
        initialOpportunity={runtime.selectedEditOpportunity}
        mutating={runtime.mutating}
        onCancel={runtime.closeEditDrawer}
        onSave={(payload) =>
          runtime.selectedEditOpportunity
            ? runtime.updateOpportunity(runtime.selectedEditOpportunity.id, payload)
            : Promise.resolve()
        }
        onSearchOrganizations={runtime.searchOrganizations}
        onSearchCustomers={runtime.searchCustomers}
        onSearchContacts={runtime.searchContacts}
      />

      <FollowupDrawer
        canUpdate={
          canUpdate && Boolean(runtime.selectedFollowupOpportunity?.accessPolicy?.canEdit)
        }
        visible={Boolean(runtime.selectedFollowupOpportunity)}
        opportunity={runtime.selectedFollowupOpportunity}
        followups={runtime.followupPage?.items ?? []}
        followupTotal={runtime.followupPage?.total ?? 0}
        followupPage={runtime.followupPage?.page ?? 1}
        followupPageSize={runtime.followupPage?.pageSize ?? 20}
        customizationProjects={runtime.customizationProjects?.items ?? []}
        followupTypeOptions={runtime.metadata?.followupTypeOptions ?? []}
        followupLoading={runtime.followupLoading}
        customizationProjectsLoading={runtime.customizationProjectsLoading}
        mutating={runtime.mutating}
        onCancel={runtime.closeFollowupDrawer}
        onPageChange={runtime.handleFollowupPageChange}
        onTabChange={runtime.setFollowupDrawerTab}
        onCreate={(payload) =>
          runtime.selectedFollowupOpportunity
            ? runtime.createFollowup(runtime.selectedFollowupOpportunity.id, payload)
            : Promise.resolve()
        }
        onDelete={(recordId) =>
          runtime.selectedFollowupOpportunity
            ? runtime.deleteFollowup(runtime.selectedFollowupOpportunity.id, recordId)
            : Promise.resolve()
        }
      />

      <SignInfoDrawer
        canUpdate={
          Boolean(canUpdate) &&
          (runtime.selectedSignInfoOpportunity?.accessPolicy?.canEdit ?? true)
        }
        visible={Boolean(runtime.selectedSignInfoOpportunity)}
        opportunity={runtime.selectedSignInfoOpportunity}
        detail={runtime.signInfoDetail}
        loading={runtime.signInfoLoading}
        mutating={runtime.mutating}
        busiSourceOptions={
          runtime.metadata?.signBusiSourceOptions?.length
            ? runtime.metadata.signBusiSourceOptions
            : CRM_OPPORTUNITY_SIGN_BUSI_SOURCE_OPTIONS
        }
        winReasonOptions={
          runtime.metadata?.signWinReasonOptions?.length
            ? runtime.metadata.signWinReasonOptions
            : CRM_OPPORTUNITY_SIGN_WIN_REASON_OPTIONS
        }
        loseReasonOptions={
          runtime.metadata?.signLoseReasonOptions?.length
            ? runtime.metadata.signLoseReasonOptions
            : CRM_OPPORTUNITY_SIGN_LOSE_REASON_OPTIONS
        }
        onCancel={runtime.closeSignInfoDrawer}
        onSave={(payload) =>
          runtime.selectedSignInfoOpportunity
            ? runtime.saveSignInfo(runtime.selectedSignInfoOpportunity.id, payload)
            : Promise.resolve()
        }
      />

      <StageDrawer
        visible={Boolean(runtime.selectedStageOpportunity)}
        opportunity={runtime.selectedStageOpportunity}
        board={runtime.stageBoard}
        stageLabelMap={stageLabelMap}
        loading={runtime.stageBoardLoading}
        canManageManualStage={runtime.selectedStageOpportunity?.accessPolicy?.canManageManualStage === true}
        mutating={runtime.mutating}
        onCancel={runtime.closeStageDrawer}
        onSaveManualStage={(payload) =>
          runtime.selectedStageOpportunity
            ? runtime.saveManualStage(runtime.selectedStageOpportunity.id, payload)
            : Promise.resolve()
        }
      />

      <StageOwnerDrawer
        canUpdate={
          canUpdate &&
          Boolean(runtime.selectedStageOwnerOpportunity?.accessPolicy?.canAssignStageOwner)
        }
        visible={Boolean(runtime.selectedStageOwnerOpportunity)}
        opportunity={runtime.selectedStageOwnerOpportunity}
        owners={runtime.stageOwners?.items ?? []}
        stageGroupOptions={runtime.metadata?.stageGroupOptions ?? []}
        staffOptions={runtime.metadata?.staffOptions ?? []}
        stageGroupLabelMap={stageGroupLabelMap}
        loading={runtime.stageOwnerLoading}
        mutating={runtime.mutating}
        onCancel={runtime.closeStageOwnerDrawer}
        onSave={(payload) =>
          runtime.selectedStageOwnerOpportunity
            ? runtime.saveStageOwners(runtime.selectedStageOwnerOpportunity.id, payload)
            : Promise.resolve()
        }
      />

      <Drawer
        className={`crm-opportunity-workbench-customization-drawer${isMobileViewport ? " is-mobile" : ""}`}
        title="关联定制"
        placement={isMobileViewport ? "bottom" : "right"}
        height={isMobileViewport ? "92vh" : undefined}
        visible={Boolean(selectedCustomizationOpportunity)}
        width={isMobileViewport ? "100%" : "calc(100vw - 48px)"}
        style={isMobileViewport ? undefined : { maxWidth: 1240 }}
        footer={null}
        unmountOnExit
        onCancel={() => setSelectedCustomizationOpportunity(null)}
      >
        {selectedCustomizationOpportunity ? (
          <div className="crm-opportunity-workbench-customization-drawer-body">
            <div className="crm-opportunity-workbench-customization-context">
              <strong>{selectedCustomizationOpportunity.opportunityName}</strong>
              <span>{selectedCustomizationOpportunity.opportunityCode}</span>
            </div>
            <CustomizationProjectListFeaturePage
              key={customizationDrawerVersion}
              embedded
              fixedFilters={{ opportunityId: selectedCustomizationOpportunity.id }}
              onOpenWorkbench={(projectId, options) => {
                const mobile = isCustomizationMobileDevice();
                const path = buildCustomizationWorkbenchEntryPath(projectId, {
                  search: mobile
                    ? buildOpportunityMobileCustomizationEntrySearch({
                        opportunityId: selectedCustomizationOpportunity.id,
                        returnTo: `${location.pathname}${location.search}`,
                        jiDiaoNo: options?.jiDiaoNo,
                      })
                    : options?.jiDiaoNo
                      ? `?JiDiaoNo=${encodeURIComponent(options.jiDiaoNo)}`
                      : undefined,
                  forceMobile: mobile ? true : undefined,
                });
                setSelectedCustomizationOpportunity(null);
                // 手机端同页进入移动定制；桌面端仍新窗打开，拦截时同页回退。
                if (mobile) {
                  navigate(path);
                  return;
                }
                if (!openCustomizationWorkbenchInNewWindow(path)) {
                  navigate(buildCustomizationWorkbenchFallbackPath(path));
                }
              }}
              onOpenBudget={(budgetOrderId) => {
                setSelectedCustomizationOpportunity(null);
                navigate(`/quote/budgets/${budgetOrderId}`);
              }}
              onOpenOrderHandover={
                canEnterOrderHandover
                  ? (item) =>
                      transferProjectToOrder(selectedCustomizationOpportunity, {
                        projectId: item.id,
                        projectName: item.name,
                      })
                  : undefined
              }
              onDeleteProject={
                selectedCustomizationOpportunity.accessPolicy?.canManageCustomizationAssignment
                  ? deleteAssociatedCustomizationProject
                  : undefined
              }
              onDeleteDemand={
                selectedCustomizationOpportunity.accessPolicy?.canManageCustomizationAssignment
                  ? deleteAssociatedCustomizationDemand
                  : undefined
              }
            />
          </div>
        ) : null}
      </Drawer>

      <Drawer
        className="crm-opportunity-workbench-find-customization-drawer"
        title="找定制"
        visible={Boolean(findCustomizationOpportunity)}
        width={680}
        footer={
          <div className="crm-opportunity-workbench-find-customization-footer">
            <span>已选择 {selectedCandidateProjectIds.length} 个</span>
            <Button
              type="primary"
              loading={reassigningCustomizations}
              disabled={
                selectedCandidateProjectIds.length === 0 || reassigningCustomizations
              }
              onClick={() => void reassignSelectedCustomizations()}
            >
              {reassigningCustomizations ? "归类中" : "确认归类"}
            </Button>
          </div>
        }
        unmountOnExit
        onCancel={() => {
          if (!reassigningCustomizations) {
            setFindCustomizationOpportunity(null);
          }
        }}
      >
        <Spin loading={findCustomizationLoading} style={{ width: "100%" }}>
          <p className="crm-opportunity-workbench-find-customization-hint">
            当前学校今年的定制均已列出，匹配度按联系人、专业、人数、天数动态排序；月份仅辅助判断，目的地差异不会自动排除。
          </p>
          <div className="crm-opportunity-workbench-find-customization-list">
            {findCustomizationCandidates.map((candidate) => {
              const checked = selectedCandidateProjectIds.includes(candidate.projectId);
              const selectable = candidate.selectable && !reassigningCustomizations;
              return (
                <label
                  key={candidate.projectId}
                  className={`crm-opportunity-workbench-find-customization-item is-${candidate.matchLevel}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={!selectable}
                    onChange={(value) => {
                      if (!candidate.selectable) {
                        return;
                      }
                      setSelectedCandidateProjectIds((current) =>
                        value
                          ? [...current, candidate.projectId]
                          : current.filter((id) => id !== candidate.projectId),
                      );
                    }}
                  />
                  <span className="crm-opportunity-workbench-find-customization-item-body">
                    <strong>{candidate.projectName || candidate.projectCode || "未命名定制"}</strong>
                    <small>
                      {candidate.projectCode || "--"} · {candidate.contactName || "联系人缺失"} · {candidate.salesUserName || "销售未配置"} · {candidate.dispatcherUserName || "计调未配置"}
                    </small>
                    <small>
                      {candidate.matchLevel === "high" ? "高匹配" : candidate.matchLevel === "review" ? "待复核" : "低匹配"} · {candidate.matchScore} 分 · {candidate.matchedDimensions.join("、") || "无一致项"}
                    </small>
                    {candidate.differentDimensions.length > 0 ? (
                      <small className="is-different">差异：{candidate.differentDimensions.join("、")}</small>
                    ) : null}
                    <small>
                      {candidate.travelMonth || "月份缺失"} · {candidate.peopleCount ?? "人数缺失"} 人 · {candidate.durationDays ?? "天数缺失"} 天 · {candidate.destinationProvinces.join("、") || "目的地缺失"}
                    </small>
                    {candidate.currentOpportunity ? <Tag color="blue">当前已归属</Tag> : candidate.opportunityCode ? <Tag color="gray">原归属 {candidate.opportunityCode}</Tag> : <Tag color="orange">未归属</Tag>}
                    {candidate.legacyWritebackReadiness === "identity_pending" ? (
                      <Tag color="orange">旧身份待治理</Tag>
                    ) : null}
                    {candidate.unselectableReason ? (
                      <small>{candidate.unselectableReason}</small>
                    ) : null}
                  </span>
                </label>
              );
            })}
            {!findCustomizationLoading && findCustomizationCandidates.length === 0 ? (
              <Empty description="暂无同校定制" />
            ) : null}
          </div>
        </Spin>
      </Drawer>

      <Drawer
        className={`crm-opportunity-workbench-add-customization-drawer${isMobileViewport ? " is-mobile" : ""}`}
        title="增定制"
        placement={isMobileViewport ? "bottom" : "right"}
        height={isMobileViewport ? "92vh" : undefined}
        visible={Boolean(addCustomizationOpportunity)}
        width={isMobileViewport ? "100%" : "calc(100vw - 48px)"}
        style={isMobileViewport ? undefined : { maxWidth: 1280 }}
        footer={null}
        unmountOnExit
        onCancel={() => setAddCustomizationOpportunity(null)}
      >
        {addCustomizationOpportunity ? (
          <div className="crm-opportunity-workbench-add-customization-drawer-body">
            <div className="crm-opportunity-workbench-customization-context">
              <strong>{addCustomizationOpportunity.opportunityName}</strong>
              <span>{addCustomizationOpportunity.opportunityCode}</span>
            </div>
            <CustomizationNewProjectTraceDraft
              key={addCustomizationOpportunity.id}
              opportunityId={addCustomizationOpportunity.id}
              embedded
              onCreated={(projectId, options) => {
                setAddCustomizationOpportunity(null);
                // 商机窄屏列表或定制手机断点：同页进入手机工作台，不走新窗。
                const mobile = isMobileViewport || isCustomizationMobileDevice();
                const path = buildCustomizationWorkbenchEntryPath(projectId, {
                  search: mobile
                    ? buildOpportunityMobileCustomizationEntrySearch({
                        opportunityId: addCustomizationOpportunity.id,
                        returnTo: `${location.pathname}${location.search}`,
                        entry: "add",
                      })
                    : undefined,
                  forceMobile: mobile ? true : undefined,
                });
                if (mobile) {
                  options?.pendingWindow?.close();
                  navigate(path);
                  return;
                }
                if (!openCustomizationWorkbenchInNewWindow(path, options?.pendingWindow)) {
                  navigate(buildCustomizationWorkbenchFallbackPath(path));
                }
              }}
            />
          </div>
        ) : null}
      </Drawer>

      <Drawer
        className="crm-opportunity-workbench-split-drawer"
        title="商机拆分"
        visible={splitOpportunityId != null}
        width="calc(100vw - 48px)"
        style={{ maxWidth: 1280 }}
        footer={null}
        unmountOnExit
        getChildrenPopupContainer={() => document.body}
        onCancel={() => setSplitOpportunityId(null)}
      >
        {splitOpportunityId != null ? (
          <div className="crm-opportunity-workbench-split-drawer-body">
            <CrmOpportunitySplitFeaturePage
              key={splitOpportunityId}
              opportunityId={splitOpportunityId}
              onClose={() => setSplitOpportunityId(null)}
              onSubmitted={() => {
                void runtime.refreshList();
              }}
            />
          </div>
        ) : null}
      </Drawer>

      <Modal
        title="确认转派商机？"
        visible={reassignConfirmVisible}
        okText="确定"
        cancelText="取消"
        confirmLoading={reassigningOwners}
        onCancel={() => {
          if (!reassigningOwners) {
            setReassignConfirmVisible(false);
          }
        }}
        onOk={() => void executeReassignSelectedOwners()}
        unmountOnExit
      >
        将所选商机转派给当前选择的新负责人，转派后原负责人不再管理此商机。
      </Modal>

      <CustomizationItineraryPreviewHost
        itineraryId={previewItineraryId}
        onClose={() => setPreviewItineraryId(null)}
      />

      {showBackToTop ? (
        <Button
          className="crm-opportunity-workbench-back-to-top"
          type="primary"
          icon={<ArrowUp size={22} />}
          aria-label="回到顶部"
          title="回到顶部"
          onClick={handleBackToTop}
        />
      ) : null}
    </main>
  );
}

type OpportunityWorkbenchRuntime = ReturnType<typeof useCrmOpportunityWorkbenchRuntime>;

/** 移动端商机筛选栏；只保留商机名称、定制号、学校。 */
function MobileOpportunityFilterBar({
  runtime,
  canCreate,
  onCreate,
  organizationOptions,
  organizationLoading,
  onSearchOrganizations,
}: {
  runtime: OpportunityWorkbenchRuntime;
  canCreate: boolean;
  onCreate: () => void;
  organizationOptions: OpportunityOrganizationOption[];
  organizationLoading: boolean;
  onSearchOrganizations: (keyword: string) => void | Promise<void>;
}) {
  /** 移动查询只提交三条件，并保留当前排序。 */
  function handleMobileSearch() {
    rememberSearchHistory(SEARCH_HISTORY_KEYS.opportunityKeyword, runtime.draftFilters.keyword ?? "");
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.opportunityCustomizationKeyword,
      runtime.draftFilters.customizationKeyword ?? "",
    );
    runtime.handleSearch({
      keyword: runtime.draftFilters.keyword,
      customizationKeyword: runtime.draftFilters.customizationKeyword,
      orgId: runtime.draftFilters.orgId,
      orgName: runtime.draftFilters.orgName,
      sortField: runtime.sortField,
      sortDirection: runtime.sortDirection,
    });
  }

  return (
    <section className="crm-opportunity-workbench-mobile-filter-panel" role="search" aria-label="移动端商机筛选">
      <div className="crm-opportunity-workbench-mobile-toolbar">
        <div className="crm-opportunity-workbench-mobile-toolbar-left">
          <h1>商机列表</h1>
        </div>
        {canCreate ? (
          <Button type="primary" size="mini" icon={<FilePlus2 size={14} />} onClick={onCreate}>
            新增
          </Button>
        ) : null}
      </div>
      <div className="crm-opportunity-workbench-mobile-filter-fields">
        <Input
          allowClear
          size="small"
          prefix={<Search size={15} />}
          aria-label="商机名称"
          placeholder="商机名称"
          value={runtime.draftFilters.keyword ?? ""}
          onChange={(keyword) => runtime.setDraftFilters((current) => ({ ...current, keyword }))}
          onPressEnter={handleMobileSearch}
        />
        <div className="crm-opportunity-workbench-mobile-filter-pair">
          <Input
            allowClear
            size="small"
            aria-label="定制号"
            placeholder="定制号"
            value={runtime.draftFilters.customizationKeyword ?? ""}
            onChange={(customizationKeyword) =>
              runtime.setDraftFilters((current) => ({ ...current, customizationKeyword }))
            }
            onPressEnter={handleMobileSearch}
          />
          <Select
            allowClear
            showSearch
            size="small"
            filterOption={false}
            loading={organizationLoading}
            aria-label="学校"
            placeholder="学校"
            dropdownMenuClassName="crm-opportunity-workbench-option-dropdown"
            triggerProps={{
              autoAlignPopupWidth: true,
              autoFitPosition: true,
              position: "bl",
            }}
            value={runtime.draftFilters.orgId}
            renderFormat={(_option, value) => {
              const orgId = Number(value);
              const matched = organizationOptions.find((option) => option.id === orgId);
              if (matched) {
                return formatOrganizationOption(matched);
              }
              return runtime.draftFilters.orgName || "";
            }}
            onFocus={() => void onSearchOrganizations("")}
            onSearch={(keyword) => void onSearchOrganizations(keyword)}
            onChange={(value) => {
              const orgId = parseOptionalSelectNumber(value);
              const organization = organizationOptions.find((option) => option.id === orgId);
              runtime.setDraftFilters((current) => ({
                ...current,
                orgId,
                orgName: organization?.orgName,
              }));
            }}
          >
            {organizationOptions.map((option) => (
              <Select.Option key={option.id} value={option.id}>
                <OptionLabel title={formatOrganizationOption(option)} />
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="crm-opportunity-workbench-mobile-filter-actions">
          <Button type="text" size="mini" onClick={runtime.handleReset}>
            重置
          </Button>
          <Button type="primary" size="mini" onClick={handleMobileSearch} aria-label="查询商机">
            查询
          </Button>
        </div>
      </div>
    </section>
  );
}

const MOBILE_OPPORTUNITY_SORT_OPTIONS = [
  { label: "最近跟进", field: "last_followup_at" },
  { label: "创建时间", field: "created_at" },
  { label: "修改时间", field: "updated_at" },
] as const;

/** 移动端排序条：最近跟进 / 创建时间 / 修改时间，交互对齐桌面双圆钮。 */
function MobileOpportunitySortBar({
  sortField,
  sortDirection,
  onSort,
}: {
  sortField: string;
  sortDirection: "asc" | "desc";
  onSort: (field: string, direction: "asc" | "desc") => void;
}) {
  return (
    <nav
      className="crm-opportunity-workbench-mobile-sort-bar"
      aria-label="商机排序"
      data-testid="opportunity-mobile-sort-bar"
    >
      {MOBILE_OPPORTUNITY_SORT_OPTIONS.map((option) => (
        <SortableHeader
          key={option.field}
          label={option.label}
          field={option.field}
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ))}
    </nav>
  );
}

/** 移动端商机列表；展示名称/商机号/最近行程与新增/修改/增定制/定制数量入口。 */
function MobileOpportunityList({
  runtime,
  canUpdate,
  canCreateCustomization,
  onOpenCustomizations,
  onAddCustomization,
  onEdit,
}: {
  runtime: OpportunityWorkbenchRuntime;
  canUpdate: boolean;
  canCreateCustomization: boolean;
  onOpenCustomizations: (item: OpportunityWorkbenchItem) => void;
  onAddCustomization: (item: OpportunityWorkbenchItem) => void;
  onEdit: (item: OpportunityWorkbenchItem) => void;
}) {
  return (
    <div className="crm-opportunity-workbench-mobile-list" data-testid="opportunity-mobile-list">
      {runtime.items.length > 0 ? (
        <Spin loading={runtime.loading}>
          <div className="crm-opportunity-workbench-mobile-card-list" role="list">
            {runtime.items.map((item) => (
              <MobileOpportunityCard
                key={item.id}
                item={item}
                canUpdate={canUpdate}
                canCreateCustomization={canCreateCustomization}
                onOpenCustomizations={onOpenCustomizations}
                onAddCustomization={onAddCustomization}
                onEdit={onEdit}
              />
            ))}
          </div>
        </Spin>
      ) : runtime.loading ? (
        <div
          className="crm-opportunity-workbench-mobile-list-status"
          data-testid="opportunity-mobile-list-loading"
          role="status"
          aria-live="polite"
          aria-label="正在加载数据"
        >
          <Spin tip="正在加载数据" />
        </div>
      ) : (
        <Empty description="暂无符合条件的商机" />
      )}
      {runtime.loading && runtime.items.length === 0 ? null : (
        <Pagination
          className="crm-opportunity-workbench-mobile-pagination"
          total={runtime.total}
          current={runtime.page}
          pageSize={runtime.pageSize}
          showTotal={(total) => `共 ${total} 条`}
          sizeCanChange={false}
          bufferSize={1}
          onChange={runtime.handlePageChange}
        />
      )}
    </div>
  );
}

/** 移动端列表行：名称/商机号/最近行程 + 修改/增定制/定制数量。 */
function MobileOpportunityCard({
  item,
  canUpdate,
  canCreateCustomization,
  onOpenCustomizations,
  onAddCustomization,
  onEdit,
}: {
  item: OpportunityWorkbenchItem;
  canUpdate: boolean;
  canCreateCustomization: boolean;
  onOpenCustomizations: (item: OpportunityWorkbenchItem) => void;
  onAddCustomization: (item: OpportunityWorkbenchItem) => void;
  onEdit: (item: OpportunityWorkbenchItem) => void;
}) {
  const disabled = item.status === "disabled";
  const canEditActive = canUpdate && item.accessPolicy?.canEdit === true && !disabled;
  const canAddCustomization =
    canCreateCustomization && item.accessPolicy?.canCreateCustomization === true && !disabled;
  const itineraryTime = formatOpportunityListDateTime(item.latestItineraryUpdatedAt);
  const itineraryProjectLabel = [
    item.latestItineraryProjectCode?.trim() || "",
    item.latestItineraryDemandVersionNumber?.trim() || "",
  ]
    .filter(Boolean)
    .join(" ");
  const hasItinerary = Boolean(itineraryTime || itineraryProjectLabel);

  return (
    <article
      className={`crm-opportunity-workbench-mobile-card${disabled ? " is-disabled" : ""}`}
      role="listitem"
    >
      <strong className="crm-opportunity-workbench-mobile-card-name" title={item.opportunityName}>
        {item.opportunityName}
      </strong>
      <div className="crm-opportunity-workbench-mobile-card-meta">
        <span
          className="crm-opportunity-workbench-mobile-card-code"
          title={item.opportunityCode}
          aria-label={`商机号 ${item.opportunityCode}`}
        >
          <span className="crm-opportunity-workbench-mobile-card-code-label">商机号</span>
          <span className="crm-opportunity-workbench-mobile-card-code-value">{item.opportunityCode}</span>
        </span>
        <time className="crm-opportunity-workbench-mobile-card-time" dateTime={item.createdAt}>
          {formatOpportunityListDateTime(item.createdAt)}
        </time>
      </div>
      {hasItinerary ? (
        <div className="crm-opportunity-workbench-mobile-card-itinerary" aria-label="最近行程">
          <span className="crm-opportunity-workbench-mobile-card-itinerary-label">最近行程</span>
          {itineraryTime ? (
            <span className="crm-opportunity-workbench-mobile-card-itinerary-time">{itineraryTime}</span>
          ) : null}
          {itineraryProjectLabel ? (
            <span
              className="crm-opportunity-workbench-mobile-card-itinerary-project"
              title={itineraryProjectLabel}
            >
              {itineraryProjectLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="crm-opportunity-workbench-mobile-card-footer">
        {canEditActive ? (
          <button
            type="button"
            className="crm-opportunity-workbench-mobile-card-action is-secondary"
            onClick={() => onEdit(item)}
          >
            修改
          </button>
        ) : null}
        {canAddCustomization ? (
          <button
            type="button"
            className="crm-opportunity-workbench-mobile-card-action is-accent"
            aria-label={`增定制 ${item.opportunityName}`}
            onClick={() => onAddCustomization(item)}
          >
            增定制
          </button>
        ) : null}
        <button
          type="button"
          className="crm-opportunity-workbench-mobile-card-action is-primary"
          aria-label={`查看定制 ${item.opportunityName} 共 ${item.customizationProjectCount} 个`}
          onClick={() => onOpenCustomizations(item)}
        >
          定制数量
          {item.customizationProjectCount > 0 ? ` ${item.customizationProjectCount}` : ""}
        </button>
      </div>
    </article>
  );
}

/** 统一封装移动断点监听，避免页面散落 resize 逻辑。 */
function useIsMobileViewport(maxWidth = 760) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener?.("change", sync);
    return () => mediaQuery.removeEventListener?.("change", sync);
  }, [maxWidth]);

  return isMobile;
}

type OpportunityEditorDraft = {
  opportunityName: string;
  opportunityNameTouched: boolean;
  orgId?: number;
  orgName?: string;
  travelMonth?: string;
  customerId?: number;
  customerName?: string;
  contactId?: number;
  contactName?: string;
  contactPositionName?: string;
  travelerCount?: number;
  studyDestination?: string;
  studyDayCount?: number;
  remark: string;
};

const emptyOpportunityEditorDraft: OpportunityEditorDraft = {
  opportunityName: "",
  opportunityNameTouched: false,
  remark: "",
};

/** 新增商机抽屉。 */
function OpportunityEditorDrawer({
  visible,
  mode = "create",
  initialOpportunity,
  mutating,
  onCancel,
  onSave,
  onSearchOrganizations,
  onSearchCustomers,
  onSearchContacts,
}: {
  visible: boolean;
  mode?: "create" | "edit";
  initialOpportunity?: OpportunityWorkbenchItem | null;
  mutating: boolean;
  onCancel: () => void;
  onSave: (payload: {
    opportunityName: string;
    orgId: number;
    remark: string;
    travelMonth?: string;
    customerId?: number;
    contactId?: number;
    travelerCount?: number;
    studyDestination?: string;
    studyDayCount?: number;
  }) => Promise<void>;
  onSearchOrganizations: (keyword: string) => Promise<OpportunityOrganizationOption[]>;
  onSearchCustomers: (orgId: number, keyword: string) => Promise<OpportunityCustomerOption[]>;
  onSearchContacts: (
    orgId: number,
    customerId: number | undefined,
    keyword: string,
  ) => Promise<OpportunityContactOption[]>;
}) {
  const [draft, setDraft] = useState<OpportunityEditorDraft>(emptyOpportunityEditorDraft);
  const [organizationOptions, setOrganizationOptions] = useState<OpportunityOrganizationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<OpportunityCustomerOption[]>([]);
  const [contactOptions, setContactOptions] = useState<OpportunityContactOption[]>([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [skipNextSuggestedNameSync, setSkipNextSuggestedNameSync] = useState(false);
  const suggestedOpportunityName = useMemo(() => buildSuggestedOpportunityName(draft), [draft]);
  const isMobileViewport = useIsMobileViewport();

  useEffect(() => {
    if (visible) {
      const nextDraft = initialOpportunity
        ? {
            opportunityName: initialOpportunity.opportunityName,
            opportunityNameTouched: false,
            orgId: initialOpportunity.orgId,
            orgName: initialOpportunity.orgName,
            travelMonth: initialOpportunity.travelMonth,
            customerId: initialOpportunity.customerId,
            customerName: initialOpportunity.customerName,
            contactId: initialOpportunity.contactId,
            contactName: initialOpportunity.contactName,
            travelerCount: initialOpportunity.travelerCount,
            studyDestination: initialOpportunity.studyDestination,
            studyDayCount: initialOpportunity.studyDayCount,
            remark: initialOpportunity.remark ?? "",
          }
        : emptyOpportunityEditorDraft;
      setDraft(nextDraft);
      setSkipNextSuggestedNameSync(Boolean(initialOpportunity));
      setOrganizationOptions(
        initialOpportunity
          ? [
              {
                id: initialOpportunity.orgId,
                orgName: initialOpportunity.orgName ?? String(initialOpportunity.orgId),
              },
            ]
          : [],
      );
      setCustomerOptions(
        initialOpportunity?.customerId
          ? [
              {
                id: initialOpportunity.customerId,
                customerName: initialOpportunity.customerName ?? String(initialOpportunity.customerId),
                orgId: initialOpportunity.orgId,
              },
            ]
          : [],
      );
      setContactOptions(
        initialOpportunity?.contactId
          ? [
              {
                id: initialOpportunity.contactId,
                contactName: initialOpportunity.contactName ?? String(initialOpportunity.contactId),
                orgId: initialOpportunity.orgId,
              },
            ]
          : [],
      );
    }
  }, [initialOpportunity, visible]);

  useEffect(() => {
    if (!visible || draft.opportunityNameTouched) {
      return;
    }
    if (skipNextSuggestedNameSync) {
      setSkipNextSuggestedNameSync(false);
      return;
    }
    setDraft((current) => {
      if (current.opportunityNameTouched || current.opportunityName === suggestedOpportunityName) {
        return current;
      }
      return { ...current, opportunityName: suggestedOpportunityName };
    });
  }, [draft.opportunityNameTouched, skipNextSuggestedNameSync, suggestedOpportunityName, visible]);

  const opportunityName = draft.opportunityName.trim();
  const remark = draft.remark.trim();
  const orgId = draft.orgId;
  const canSubmit = Boolean(opportunityName && remark && orgId);

  /** 保存新增商机表单。 */
  async function handleSave() {
    if (!canSubmit || !orgId) {
      return;
    }
    const payload: {
      opportunityName: string;
      orgId: number;
      remark: string;
      travelMonth?: string;
      customerId?: number;
      contactId?: number;
      travelerCount?: number;
      studyDestination?: string;
      studyDayCount?: number;
    } = {
      opportunityName,
      orgId,
      remark,
    };
    if (draft.travelMonth) {
      payload.travelMonth = draft.travelMonth;
    }
    if (draft.customerId) {
      payload.customerId = draft.customerId;
    }
    if (draft.contactId) {
      payload.contactId = draft.contactId;
    }
    if (draft.travelerCount) {
      payload.travelerCount = draft.travelerCount;
    }
    if (draft.studyDestination?.trim()) {
      payload.studyDestination = draft.studyDestination.trim();
    }
    if (draft.studyDayCount) {
      payload.studyDayCount = draft.studyDayCount;
    }
    try {
      await onSave(payload);
    } catch {
      // Mutation runtime 已展示后端业务详情；保留抽屉供用户修正后重试。
      return;
    }
  }

  /** 搜索学校候选并写入下拉选项。 */
  async function handleSearchOrganizations(keyword: string) {
    setOrganizationLoading(true);
    try {
      setOrganizationOptions(await onSearchOrganizations(keyword));
    } finally {
      setOrganizationLoading(false);
    }
  }

  /** 搜索已选学校下的客户候选。 */
  async function handleSearchCustomers(orgIdValue: number, keyword: string) {
    setCustomerLoading(true);
    try {
      setCustomerOptions(await onSearchCustomers(orgIdValue, keyword));
    } finally {
      setCustomerLoading(false);
    }
  }

  /** 搜索已选学校下的联系人候选。 */
  async function handleSearchContacts(
    orgIdValue: number,
    customerIdValue: number | undefined,
    keyword: string,
  ) {
    setContactLoading(true);
    try {
      setContactOptions(await onSearchContacts(orgIdValue, customerIdValue, keyword));
    } finally {
      setContactLoading(false);
    }
  }

  /** 选择学校后清空客户和联系人，并预加载联级候选。 */
  function handleOrganizationChange(value: unknown) {
    const nextOrgId = parseOptionalSelectNumber(value);
    const nextOrganization = organizationOptions.find((option) => option.id === nextOrgId);
    setDraft((current) => ({
      ...current,
      orgId: nextOrgId,
      orgName: nextOrganization?.orgName,
      customerId: undefined,
      customerName: undefined,
      contactId: undefined,
      contactName: undefined,
    }));
    setCustomerOptions([]);
    setContactOptions([]);
    if (nextOrgId) {
      void handleSearchCustomers(nextOrgId, "");
      void handleSearchContacts(nextOrgId, undefined, "");
    }
  }

  /** 选择客户后只更新客户，不影响学校维度联系人候选。 */
  function handleCustomerChange(value: unknown) {
    const nextCustomerId = parseOptionalSelectNumber(value);
    const nextCustomer = customerOptions.find((option) => option.id === nextCustomerId);
    setDraft((current) => ({
      ...current,
      customerId: nextCustomerId,
      customerName: nextCustomer?.customerName,
    }));
  }

  return (
    <Drawer
      className={`crm-opportunity-workbench-drawer${isMobileViewport ? " is-mobile" : ""}`}
      title={mode === "edit" ? "修改商机" : "新增商机"}
      placement={isMobileViewport ? "bottom" : "right"}
      height={isMobileViewport ? "92vh" : undefined}
      width={isMobileViewport ? "100%" : 620}
      visible={visible}
      autoFocus={false}
      onCancel={onCancel}
      footer={
        <div className="crm-opportunity-workbench-drawer-footer">
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            icon={<Save size={15} />}
            disabled={!canSubmit}
            loading={mutating}
            onClick={() => void handleSave()}
          >
            {mode === "edit" ? "保存修改" : "保存商机"}
          </Button>
        </div>
      }
    >
      <div className="crm-opportunity-workbench-editor-form admin-editor-form">
        <section className="crm-opportunity-workbench-editor-section">
          <Typography.Text className="crm-opportunity-workbench-section-title">
            最小建商机信息
          </Typography.Text>
          <label className="crm-opportunity-workbench-field">
            <span>
              <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                *
              </i>
              学校
            </span>
            <Select
              allowClear
              showSearch
              filterOption={false}
              loading={organizationLoading}
              dropdownMenuClassName="crm-opportunity-workbench-option-dropdown"
              dropdownMenuStyle={{ minWidth: isMobileViewport ? undefined : 420 }}
              triggerProps={{
                autoAlignPopupWidth: isMobileViewport,
                autoFitPosition: true,
                boundaryDistance: { right: 8, bottom: 8 },
                position: isMobileViewport ? "bl" : "br",
              }}
              placeholder="搜索学校名称"
              value={draft.orgId}
              renderFormat={(_option, value) => {
                const orgId = Number(value);
                const matched = organizationOptions.find((option) => option.id === orgId);
                if (matched) {
                  return formatOrganizationOption(matched);
                }
                return draft.orgName || "";
              }}
              onFocus={() => void handleSearchOrganizations("")}
              onSearch={(keyword) => void handleSearchOrganizations(keyword)}
              onChange={handleOrganizationChange}
            >
              {organizationOptions.map((option) => (
                <Select.Option key={option.id} value={option.id} className="crm-opportunity-workbench-long-option">
                  <OptionLabel title={formatOrganizationOption(option)} />
                </Select.Option>
              ))}
            </Select>
          </label>
          <label className="crm-opportunity-workbench-field">
            <span>
              <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                *
              </i>
              备注
            </span>
            <Input.TextArea
              autoSize={{ minRows: 4, maxRows: 7 }}
              placeholder="记录早期线索、学院/系、老师原话或不确定信息"
              value={draft.remark}
              onChange={(remark) => setDraft((current) => ({ ...current, remark }))}
            />
          </label>
        </section>
        <section className="crm-opportunity-workbench-editor-section">
          <Typography.Text className="crm-opportunity-workbench-section-title">
            可后补信息
          </Typography.Text>
          <div className="crm-opportunity-workbench-editor-grid">
            <label className="crm-opportunity-workbench-field">
              <span>出行月份</span>
              <div
                onChangeCapture={(event) => {
                  const target = event.target as HTMLInputElement;
                  const travelMonth = normalizeTravelMonthInput(target.value);
                  setDraft((current) => ({
                    ...current,
                    travelMonth,
                  }));
                }}
              >
                <DatePicker.MonthPicker
                  allowClear
                  format="YYYY-MM"
                  placeholder="选择出行月份"
                  value={draft.travelMonth}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      travelMonth: typeof value === "string" && value ? value : undefined,
                    }))
                  }
                />
              </div>
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>预计人数</span>
              <InputNumber
                min={1}
                precision={0}
                mode="button"
                placeholder="预计人数"
                value={draft.travelerCount}
                onChange={(travelerCount) =>
                  setDraft((current) => ({
                    ...current,
                    travelerCount: parseOptionalInputNumber(travelerCount),
                  }))
                }
              />
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>客户</span>
              <Select
                allowClear
                showSearch
                filterOption={false}
                loading={customerLoading}
                disabled={!draft.orgId}
                dropdownMenuClassName="crm-opportunity-workbench-option-dropdown"
                dropdownMenuStyle={{ minWidth: isMobileViewport ? undefined : 440 }}
                triggerProps={{
                  autoAlignPopupWidth: isMobileViewport,
                  autoFitPosition: true,
                  boundaryDistance: { right: 8, bottom: 8 },
                  position: isMobileViewport ? "bl" : "br",
                }}
                placeholder={draft.orgId ? "搜索该学校下客户" : "请先选择学校"}
                value={draft.customerId}
                renderFormat={(_option, value) => {
                  const customerId = Number(value);
                  const matched = customerOptions.find((option) => option.id === customerId);
                  return matched?.customerName || draft.customerName || "";
                }}
                onFocus={() =>
                  draft.orgId ? void handleSearchCustomers(draft.orgId, "") : undefined
                }
                onSearch={(keyword) =>
                  draft.orgId ? void handleSearchCustomers(draft.orgId, keyword) : undefined
                }
                onChange={handleCustomerChange}
              >
                {customerOptions.map((option) => (
                  <Select.Option key={option.id} value={option.id} className="crm-opportunity-workbench-long-option">
                    <OptionLabel title={option.customerName} />
                  </Select.Option>
                ))}
              </Select>
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>联系人</span>
              <Select
                allowClear
                showSearch
                filterOption={false}
                loading={contactLoading}
                disabled={!draft.orgId}
                dropdownMenuClassName="crm-opportunity-workbench-option-dropdown"
                dropdownMenuStyle={{ minWidth: isMobileViewport ? undefined : 420 }}
                triggerProps={{
                  autoAlignPopupWidth: isMobileViewport,
                  autoFitPosition: true,
                  boundaryDistance: { right: 8, bottom: 8 },
                  position: isMobileViewport ? "bl" : "br",
                }}
                placeholder={draft.orgId ? "搜索该学校下联系人" : "请先选择学校"}
                value={draft.contactId}
                renderFormat={(_option, value) => {
                  const contactId = Number(value);
                  const matched = contactOptions.find((option) => option.id === contactId);
                  return matched ? formatContactOption(matched) : draft.contactName || "";
                }}
                onFocus={() =>
                  draft.orgId
                    ? void handleSearchContacts(draft.orgId, undefined, "")
                    : undefined
                }
                onSearch={(keyword) =>
                  draft.orgId
                    ? void handleSearchContacts(draft.orgId, undefined, keyword)
                    : undefined
                }
                onChange={(value) => {
                  const nextContactId = parseOptionalSelectNumber(value);
                  const nextContact = contactOptions.find((option) => option.id === nextContactId);
                  setDraft((current) => ({
                    ...current,
                    contactId: nextContactId,
                    contactName: nextContact?.contactName,
                    contactPositionName: nextContact?.positionName,
                  }));
                }}
              >
                {contactOptions.map((option) => (
                  <Select.Option key={option.id} value={option.id} className="crm-opportunity-workbench-long-option">
                    <OptionLabel title={formatContactOption(option)} />
                  </Select.Option>
                ))}
              </Select>
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>考察目的地</span>
              <Input
                allowClear
                placeholder="如：贵州、敦煌、北京"
                value={draft.studyDestination ?? ""}
                onChange={(studyDestination) =>
                  setDraft((current) => ({ ...current, studyDestination }))
                }
              />
            </label>
            <label className="crm-opportunity-workbench-field">
              <span>课程学习天数</span>
              <InputNumber
                min={1}
                max={365}
                precision={0}
                mode="button"
                placeholder="如：10"
                value={draft.studyDayCount}
                onChange={(studyDayCount) =>
                  setDraft((current) => ({
                    ...current,
                    studyDayCount: parseOptionalInputNumber(studyDayCount),
                  }))
                }
              />
            </label>
        </div>
      </section>
        <section className="crm-opportunity-workbench-name-panel">
          <div>
            <Typography.Text className="crm-opportunity-workbench-section-title">
              <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                *
              </i>
              商机名称
            </Typography.Text>
            <span>系统建议，可手动修改</span>
          </div>
          <Input
            placeholder="系统会根据已选信息生成商机名"
            value={draft.opportunityName}
            onChange={(opportunityName) =>
              setDraft((current) => ({
                ...current,
                opportunityName,
                opportunityNameTouched: true,
              }))
            }
          />
        </section>
      </div>
    </Drawer>
  );
}

/** 构建商机列表表格列。 */
function buildOpportunityColumns({
  visibleColumns,
  canUpdate,
  canCreateCustomization,
  canFindCustomization,
  canSplitOpportunity,
  stageLabelMap,
  statusLabelMap,
  sortField,
  sortDirection,
  onOpenStage,
  onSort,
  onOpenFollowup,
  onOpenSignInfo,
  onEdit,
  onDisable,
  onEnable,
  onOpenCustomizations,
  onOpenLatestItinerary,
  onAddCustomization,
  onSplit,
  onFindCustomizations,
  onOpenStageOwners,
}: {
  visibleColumns: OpportunityWorkbenchColumnKey[];
  canUpdate: boolean;
  canCreateCustomization: boolean;
  canFindCustomization: boolean;
  canSplitOpportunity: boolean;
  stageLabelMap: Map<string, string>;
  statusLabelMap: Map<string, string>;
  onOpenStage: (item: OpportunityWorkbenchItem) => void;
  sortField: string;
  sortDirection: "asc" | "desc";
  onSort: (field: string, direction: "asc" | "desc") => void;
  onOpenFollowup: (item: OpportunityWorkbenchItem) => void;
  onOpenSignInfo: (item: OpportunityWorkbenchItem) => void;
  onEdit: (item: OpportunityWorkbenchItem) => void;
  onDisable: (item: OpportunityWorkbenchItem) => void;
  onEnable: (item: OpportunityWorkbenchItem) => void;
  onOpenCustomizations: (item: OpportunityWorkbenchItem) => void;
  onOpenLatestItinerary: (item: OpportunityWorkbenchItem) => void;
  onAddCustomization: (item: OpportunityWorkbenchItem) => void;
  onSplit: (item: OpportunityWorkbenchItem) => void;
  onFindCustomizations: (item: OpportunityWorkbenchItem) => void;
  onOpenStageOwners: (item: OpportunityWorkbenchItem) => void;
}): TableColumnProps<OpportunityWorkbenchItem>[] {
  const visibleSet = new Set(visibleColumns);
  const columnByKey = new Map<
    OpportunityWorkbenchColumnKey,
    TableColumnProps<OpportunityWorkbenchItem>
  >();

  if (visibleSet.has("opportunity")) {
    columnByKey.set("opportunity", {
      title: "商机",
      fixed: "left",
      width: 240,
      render: (_, item) => (
        <div className="crm-opportunity-workbench-main-cell">
          <strong>{item.opportunityName}</strong>
          <div className="crm-opportunity-workbench-main-cell__meta">
            <span>{item.opportunityCode}</span>
            <Tag
              color={
                item.status === "open"
                  ? "arcoblue"
                  : item.status === "won"
                    ? "green"
                    : item.status === "lost"
                      ? "red"
                      : "gray"
              }
              size="small"
            >
              {statusLabelMap.get(item.status) ?? item.status}
            </Tag>
          </div>
        </div>
      ),
    });
  }
  if (visibleSet.has("orgCustomer")) {
    columnByKey.set("orgCustomer", {
      title: "学校/客户",
      width: 240,
      render: (_, item) => (
        <div className="crm-opportunity-workbench-stack-cell">
          <strong>{item.orgName ?? "-"}</strong>
          <span>{item.customerName ?? "客户待补齐"}</span>
          {item.contactName ? <span>联系人：{item.contactName}</span> : null}
        </div>
      ),
    });
  }
  if (visibleSet.has("signInfo")) {
    columnByKey.set("signInfo", {
      title: "商机进度",
      width: 88,
      align: "center",
      render: (_, item) => {
        const signInfo = item.signInfo ?? { status: "unfilled" as const };
        const progressText = signInfo.opportunityProgress || "50%";
        if (signInfo.status === "unfilled") {
          return (
            <button
              type="button"
              className="crm-opportunity-workbench-sign-info-chip is-unfilled"
              aria-label={`填写商机进度 ${item.opportunityName}`}
              onClick={() => onOpenSignInfo(item)}
            >
              {progressText === "100%" ? "80%" : progressText}
            </button>
          );
        }
        const isWon = signInfo.status === "won";
        const summaryText = isWon
          ? signInfo.teamSummary?.trim() || "-"
          : signInfo.reasonRemark?.trim() || "-";
        const tooltipContent = (
          <div className="crm-opportunity-workbench-sign-info-tooltip">
            <div className="crm-opportunity-workbench-sign-info-tooltip__row">
              <span>商机进度</span>
              <strong>{progressText}</strong>
            </div>
            <div className="crm-opportunity-workbench-sign-info-tooltip__row">
              <span>客户来源</span>
              <strong>{signInfo.busiSourceType || "-"}</strong>
            </div>
            <div className="crm-opportunity-workbench-sign-info-tooltip__row">
              <span>{isWon ? "赢单类型" : "输单类型"}</span>
              <strong>{signInfo.reasonType || "-"}</strong>
            </div>
            {signInfo.decisionMakerText ? (
              <div className="crm-opportunity-workbench-sign-info-tooltip__row">
                <span>拍板人</span>
                <strong>{signInfo.decisionMakerText}</strong>
              </div>
            ) : null}
            <div className="crm-opportunity-workbench-sign-info-tooltip__summary">
              <span>{isWon ? "回团总结" : "输单总结"}</span>
              <p>{summaryText}</p>
            </div>
            {!isWon ? (
              <div className="crm-opportunity-workbench-sign-info-tooltip__summary">
                <span>下次如何拿下</span>
                <p>{signInfo.competitorReason?.trim() || "-"}</p>
              </div>
            ) : null}
          </div>
        );
        return (
          <Tooltip content={tooltipContent}>
            <span
              className={`crm-opportunity-workbench-sign-info-chip ${isWon ? "is-won" : "is-lost"}`}
            >
              {isWon ? "赢单" : "输单"}
            </span>
          </Tooltip>
        );
      },
    });
  }
  if (visibleSet.has("monthCount")) {
    columnByKey.set("monthCount", {
      title: "月份/人数",
      width: 136,
      align: "center",
      render: (_, item) => (
        <span className="crm-opportunity-workbench-month-count">
          {`${item.travelMonth ?? "-"} / ${item.travelerCount ? `${item.travelerCount}人` : "-"}`}
        </span>
      ),
    });
  }
  if (visibleSet.has("stage")) {
    columnByKey.set("stage", {
      title: "当前阶段",
      width: 126,
      align: "center",
      render: (_, item) => (
        <div className="crm-opportunity-workbench-stage-cell">
          <Button
            size="mini"
            className="crm-opportunity-workbench-stage-button"
            icon={<History size={14} />}
            aria-label={`阶段 ${stageLabelMap.get(item.stage) ?? "阶段待设置"}`}
            onClick={() => onOpenStage(item)}
          >
            {stageLabelMap.get(item.stage) ?? "阶段待设置"}
          </Button>
          {item.stageStalled ? (
            <Tag color="orangered" size="small" className="crm-opportunity-workbench-stage-stalled-tag">
              停滞{item.stageStalledDays}天
            </Tag>
          ) : null}
        </div>
      ),
    });
  }
  if (visibleSet.has("customization")) {
    columnByKey.set("customization", {
      title: "关联定制",
      width: 112,
      align: "center",
      render: (_, item) => (
        <div className="crm-opportunity-workbench-customization-cell">
          {item.selectedCustomizationProjectName ? (
            <Tag color="arcoblue">已采用：{item.selectedCustomizationProjectName}</Tag>
          ) : null}
          {item.customizationProjectCount > 0 ? (
            <Button
              type="text"
              size="mini"
              aria-label={`查看定制 ${item.opportunityName} 共 ${item.customizationProjectCount} 个`}
              onClick={() => onOpenCustomizations(item)}
            >
              定制 {item.customizationProjectCount} 个
            </Button>
          ) : (
            <span className="crm-opportunity-workbench-empty-customization">暂无定制</span>
          )}
        </div>
      ),
    });
  }
  if (visibleSet.has("itinerary")) {
    columnByKey.set("itinerary", {
      title: "最近行程",
      width: 168,
      align: "center",
      render: (_, item) => {
        const timeLabel = formatOpportunityListDateTime(item.latestItineraryUpdatedAt);
        const projectCode = item.latestItineraryProjectCode?.trim() || "";
        const versionNumber = item.latestItineraryDemandVersionNumber?.trim() || "";
        const projectLabel = [projectCode, versionNumber].filter(Boolean).join(" ");
        const itineraryId = item.latestItineraryId?.trim();
        const tooltipContent = buildLatestItineraryTooltip(item);
        const cellBody =
          itineraryId && timeLabel ? (
            <Button
              type="text"
              size="mini"
              className="crm-opportunity-workbench-latest-itinerary-link"
              aria-label={`打开最近行程 ${timeLabel}${projectLabel ? ` ${projectLabel}` : ""}`}
              onClick={() => onOpenLatestItinerary(item)}
            >
              <span>{timeLabel}</span>
              {projectLabel ? <small>{projectLabel}</small> : null}
            </Button>
          ) : (
            <>
              <span>{timeLabel || "--"}</span>
              {projectLabel ? <small>{projectLabel}</small> : null}
            </>
          );
        return (
          <div className="crm-opportunity-workbench-latest-itinerary-cell">
            {tooltipContent ? (
              <Tooltip content={tooltipContent} position="top">
                <div className="crm-opportunity-workbench-latest-itinerary-hover">{cellBody}</div>
              </Tooltip>
            ) : (
              cellBody
            )}
          </div>
        );
      },
    });
  }
  if (visibleSet.has("followup")) {
    columnByKey.set("followup", {
      title: (
        <SortableHeader
          label="最近跟进"
          field="last_followup_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 168,
      align: "center",
      dataIndex: "last_followup_at",
      render: (_, item) => {
        const latestContent = item.latestFollowupContent ?? "暂无跟进记录";
        const latestSummary = truncateText(latestContent, 12);
        return (
          <div className="crm-opportunity-workbench-followup-cell">
            <Tooltip content={latestContent}>
              <button
                type="button"
                className="crm-opportunity-workbench-followup-link"
                aria-label={latestContent}
                onClick={() => onOpenFollowup(item)}
              >
                {latestSummary}
              </button>
            </Tooltip>
            <small title={item.latestFollowupAt}>
              {item.latestFollowupAt
                ? formatOpportunityListDateTime(item.latestFollowupAt)
                : "暂无跟进时间"}
            </small>
          </div>
        );
      },
    });
  }
  if (visibleSet.has("owners")) {
    columnByKey.set("owners", {
      title: "负责人",
      width: 96,
      align: "center",
      render: (_, item) => item.ownerUserName ?? "-",
    });
  }
  if (visibleSet.has("createdAt")) {
    columnByKey.set("createdAt", {
      title: (
        <SortableHeader
          label="创建时间"
          field="created_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "created_at",
      width: 168,
      align: "center",
      render: (_, item) => (
        <span className="crm-opportunity-workbench-time-text" title={item.createdAt}>
          {formatOpportunityListDateTime(item.createdAt)}
        </span>
      ),
    });
  }
  if (visibleSet.has("updatedAt")) {
    columnByKey.set("updatedAt", {
      title: (
        <SortableHeader
          label="修改时间"
          field="updated_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "updated_at",
      width: 168,
      align: "center",
      render: (_, item) => (
        <span className="crm-opportunity-workbench-time-text" title={item.updatedAt}>
          {formatOpportunityListDateTime(item.updatedAt)}
        </span>
      ),
    });
  }
  if (visibleSet.has("actions")) {
    columnByKey.set("actions", {
      title: "操作",
      fixed: "right",
      width: 88,
      render: (_, item) => {
        const disabled = item.status === "disabled";
        const hasEditPermission = canUpdate && item.accessPolicy?.canEdit === true;
        const canEditActive = hasEditPermission && !disabled;
        const canAddCustomization =
          canCreateCustomization &&
          item.accessPolicy?.canCreateCustomization === true &&
          !disabled;
        const primaryActions: ReactNode[] = [];
        const overflowItems: ReactElement[] = [];

        if (canEditActive) {
          primaryActions.push(
            <Tooltip key="edit" content="修改">
              <Button
                size="mini"
                shape="circle"
                type="outline"
                className="crm-opportunity-workbench-row-icon-btn is-edit"
                aria-label={`修改商机 ${item.opportunityName}`}
                icon={<Pencil size={14} />}
                onClick={() => onEdit(item)}
              />
            </Tooltip>,
          );
        }

        if (canAddCustomization) {
          primaryActions.push(
            <Button
              key="add-customization"
              type="outline"
              size="mini"
              className="crm-opportunity-workbench-row-action-tag"
              aria-label={`增定制 ${item.opportunityName}`}
              onClick={() => onAddCustomization(item)}
            >
              增定制
            </Button>,
          );
        }

        if (hasEditPermission) {
          if (disabled) {
            overflowItems.push(
              <Menu.Item
                key="enable"
                aria-label={`启用商机 ${item.opportunityName}`}
                onClick={() => onEnable(item)}
              >
                启用
              </Menu.Item>,
            );
          } else {
            overflowItems.push(
              <Menu.Item
                key="disable"
                aria-label={`停用商机 ${item.opportunityName}`}
                onClick={() => {
                  Modal.confirm({
                    title: "确认停用该商机吗？",
                    content: `停用「${item.opportunityName}」后默认列表不再展示，可在状态筛选中选「已停用」后再启用。`,
                    okText: "停用",
                    cancelText: "取消",
                    okButtonProps: { status: "danger" },
                    onOk: () => onDisable(item),
                  });
                }}
              >
                停用
              </Menu.Item>,
            );
          }
        }

        if (
          !disabled &&
          canSplitOpportunity &&
          item.customizationProjectCount >= 2 &&
          item.accessPolicy?.canSplitOpportunity
        ) {
          overflowItems.push(
            <Menu.Item
              key="split-opportunity"
              aria-label={`拆分商机 ${item.opportunityName}`}
              onClick={() => onSplit(item)}
            >
              拆分
            </Menu.Item>,
          );
        }

        if (
          !disabled &&
          canFindCustomization &&
          item.accessPolicy?.canManageCustomizationAssignment
        ) {
          overflowItems.push(
            <Menu.Item
              key="find-customization"
              aria-label={`找定制 ${item.opportunityName}`}
              onClick={() => onFindCustomizations(item)}
            >
              找定制
            </Menu.Item>,
          );
        }

        if (!disabled && canUpdate && item.accessPolicy?.canAssignStageOwner) {
          overflowItems.push(
            <Menu.Item
              key="stage-owners"
              aria-label={`阶段负责人 ${item.opportunityName}`}
              onClick={() => onOpenStageOwners(item)}
            >
              阶段负责人
            </Menu.Item>,
          );
        }

        return (
          <div className="crm-opportunity-workbench-row-actions" data-testid="opportunity-row-actions">
            {primaryActions}
            {overflowItems.length > 0 ? (
              <Dropdown
                trigger="click"
                position="br"
                droplist={<Menu>{overflowItems}</Menu>}
              >
                <Button
                  size="mini"
                  type="text"
                  className="crm-opportunity-workbench-row-more-btn"
                  icon={<MoreHorizontal size={16} />}
                  aria-label={`更多操作 ${item.opportunityName}`}
                />
              </Dropdown>
            ) : null}
          </div>
        );
      },
    });
  }

  return visibleColumns
    .map((key) => columnByKey.get(key))
    .filter((column): column is TableColumnProps<OpportunityWorkbenchItem> => Boolean(column));
}


/** 可直接指定升序或降序的表头，与客户工作台保持一致。 */
function SortableHeader({
  label,
  field,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: string;
  activeField?: string;
  activeDirection: "asc" | "desc";
  onSort: (field: string, direction: "asc" | "desc") => void;
}) {
  const active = activeField === field;
  return (
    <div className="crm-opportunity-workbench-sort-header">
      <span>{label}</span>
      <span className="crm-opportunity-workbench-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}
/** 商机输赢单填写抽屉。 */
function SignInfoDrawer({
  canUpdate,
  visible,
  opportunity,
  detail,
  loading,
  mutating,
  busiSourceOptions,
  winReasonOptions,
  loseReasonOptions,
  onCancel,
  onSave,
}: {
  canUpdate: boolean;
  visible: boolean;
  opportunity: OpportunityWorkbenchItem | null;
  detail?: OpportunitySignInfoDetail;
  loading: boolean;
  mutating: boolean;
  busiSourceOptions: Array<{ value: string; label: string }>;
  winReasonOptions: Array<{ value: string; label: string }>;
  loseReasonOptions: Array<{ value: string; label: string }>;
  onCancel: () => void;
  onSave: (payload: OpportunitySignInfoSavePayload) => Promise<void>;
}) {
  const [choice, setChoice] = useState<OpportunitySignInfoChoice>("50%");
  const [keptProgress, setKeptProgress] = useState<OpportunityProgress>("50%");
  const [busiSourceType, setBusiSourceType] = useState<string | undefined>();
  const [reasonType, setReasonType] = useState<string | undefined>();
  const [decisionMakerText, setDecisionMakerText] = useState("");
  const [teamSummary, setTeamSummary] = useState("");
  const [reasonRemark, setReasonRemark] = useState("");
  const [competitorReason, setCompetitorReason] = useState("");

  useEffect(() => {
    if (!visible) {
      return;
    }
    const nextProgress =
      detail?.opportunityProgress === "80%" || detail?.opportunityProgress === "100%"
        ? "80%"
        : "50%";
    setKeptProgress(nextProgress);
    if (detail?.status === "won") {
      setChoice("won");
    } else if (detail?.status === "lost") {
      setChoice("lost");
    } else {
      setChoice(nextProgress);
    }
    setBusiSourceType(detail?.busiSourceType);
    setReasonType(detail?.reasonType);
    setDecisionMakerText(detail?.decisionMakerText ?? "");
    setTeamSummary(detail?.teamSummary ?? "");
    setReasonRemark(detail?.reasonRemark ?? "");
    setCompetitorReason(detail?.competitorReason ?? "");
  }, [detail, visible]);

  const showSignForm = choice === "won" || choice === "lost";
  const isSign = choice === "won";
  const reasonOptions = isSign ? winReasonOptions : loseReasonOptions;
  const resolvedBusiSourceOptions =
    busiSourceOptions.length > 0
      ? busiSourceOptions
      : CRM_OPPORTUNITY_SIGN_BUSI_SOURCE_OPTIONS;
  const resolvedReasonOptions =
    reasonOptions.length > 0
      ? reasonOptions
      : isSign
        ? CRM_OPPORTUNITY_SIGN_WIN_REASON_OPTIONS
        : CRM_OPPORTUNITY_SIGN_LOSE_REASON_OPTIONS;

  /** 切换商机进度四选一；切到赢/输单时清空类型以免串值。 */
  function handleChoiceChange(next: OpportunitySignInfoChoice) {
    if (next !== choice && (next === "won" || next === "lost")) {
      setReasonType(undefined);
    }
    setChoice(next);
    if (next === "50%" || next === "80%") {
      setKeptProgress(next);
    }
  }

  /** 保存商机进度：仅百分比或完整赢/输单表单。 */
  async function handleSave() {
    if (!showSignForm) {
      await onSave({
        opportunityProgress: choice === "80%" ? "80%" : "50%",
      });
      return;
    }
    if (!busiSourceType) {
      Message.warning("请选择客户来源");
      return;
    }
    if (!reasonType) {
      Message.warning(isSign ? "请选择赢单类型" : "请选择输单类型");
      return;
    }
    if (isSign && !teamSummary.trim()) {
      Message.warning("请填写回团总结");
      return;
    }
    if (!isSign && !reasonRemark.trim()) {
      Message.warning("请填写输单总结");
      return;
    }
    if (!isSign && !competitorReason.trim()) {
      Message.warning("请填写下次如何拿下");
      return;
    }
    await onSave({
      isSign,
      opportunityProgress: keptProgress,
      busiSourceType,
      reasonType,
      decisionMakerText: decisionMakerText.trim() || undefined,
      teamSummary: isSign ? teamSummary.trim() : undefined,
      reasonRemark: isSign ? undefined : reasonRemark.trim(),
      competitorReason: isSign ? undefined : competitorReason.trim(),
    });
  }

  return (
    <Drawer
      className="crm-opportunity-workbench-drawer crm-opportunity-workbench-sign-info-drawer"
      title="商机进度"
      width={520}
      visible={visible}
      onCancel={onCancel}
      footer={
        <div className="crm-opportunity-workbench-drawer-footer">
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            icon={<Save size={15} />}
            loading={mutating}
            disabled={!canUpdate}
            onClick={() => void handleSave()}
          >
            保存商机进度
          </Button>
        </div>
      }
    >
      <Spin loading={loading} style={{ width: "100%" }}>
        <div className="crm-opportunity-workbench-drawer-body">
          <div className="crm-opportunity-workbench-drawer-title">
            <strong title={opportunity?.opportunityName}>{opportunity?.opportunityName ?? "-"}</strong>
            <span>
              {opportunity?.opportunityCode ?? "-"}
              {opportunity?.orgName ? ` · ${opportunity.orgName}` : ""}
            </span>
          </div>

          <section className="crm-opportunity-workbench-sign-info-form">
            <Typography.Text className="crm-opportunity-workbench-section-title">
              商机进度
            </Typography.Text>
            <div
              className="crm-opportunity-workbench-sign-info-progress"
              role="radiogroup"
              aria-label="商机进度"
            >
              {CRM_OPPORTUNITY_PROGRESS_CHOICES.map((option) => {
                const isActive = choice === option.value;
                const toneClass =
                  option.value === "won"
                    ? "is-won"
                    : option.value === "lost"
                      ? "is-lost"
                      : "is-progress";
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`crm-opportunity-workbench-sign-info-result__option ${toneClass}${
                      isActive ? " is-active" : ""
                    }`}
                    aria-pressed={isActive}
                    disabled={!canUpdate}
                    onClick={() => handleChoiceChange(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {showSignForm ? (
              <>
            <div className="crm-opportunity-workbench-field">
              <span>拍板人</span>
              <Input
                value={decisionMakerText}
                placeholder="请输入此团输赢关键人的信息"
                maxLength={500}
                onChange={setDecisionMakerText}
                disabled={!canUpdate}
              />
            </div>

            <div className="crm-opportunity-workbench-field">
              <span>
                <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                  *
                </i>
                客户来源
              </span>
              <Select
                placeholder="请选择"
                value={busiSourceType}
                onChange={(value) => setBusiSourceType(String(value))}
                disabled={!canUpdate}
                allowClear
                getPopupContainer={() => document.body}
                triggerProps={{ autoFitPosition: true, position: "bl" }}
              >
                {resolvedBusiSourceOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="crm-opportunity-workbench-field">
              <span>
                <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                  *
                </i>
                {isSign ? "赢单类型" : "输单类型"}
              </span>
              <Select
                placeholder="请选择"
                value={reasonType}
                onChange={(value) => setReasonType(String(value))}
                disabled={!canUpdate}
                allowClear
                getPopupContainer={() => document.body}
                triggerProps={{ autoFitPosition: true, position: "bl" }}
              >
                {resolvedReasonOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="crm-opportunity-workbench-field">
              <span>
                <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                  *
                </i>
                {isSign ? "回团总结" : "输单总结"}
              </span>
              <Input.TextArea
                value={isSign ? teamSummary : reasonRemark}
                placeholder={isSign ? "填写回团总结" : "填写输单总结"}
                maxLength={5000}
                autoSize={{ minRows: 5, maxRows: 10 }}
                onChange={(value) => {
                  if (isSign) {
                    setTeamSummary(value);
                  } else {
                    setReasonRemark(value);
                  }
                }}
                disabled={!canUpdate}
              />
            </div>

            {!isSign ? (
              <div className="crm-opportunity-workbench-field">
                <span>
                  <i className="crm-opportunity-workbench-field-required" aria-hidden="true">
                    *
                  </i>
                  下次如何拿下
                </span>
                <Input.TextArea
                  value={competitorReason}
                  placeholder="填写下次如何做才能签团"
                  maxLength={5000}
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  onChange={setCompetitorReason}
                  disabled={!canUpdate}
                />
              </div>
            ) : null}
              </>
            ) : null}
          </section>
        </div>
      </Spin>
    </Drawer>
  );
}

/** 商机跟进记录与需求追溯摘要抽屉。 */
function FollowupDrawer({
  canUpdate,
  visible,
  opportunity,
  followups,
  followupTotal,
  followupPage,
  followupPageSize,
  customizationProjects,
  followupTypeOptions,
  followupLoading,
  customizationProjectsLoading,
  mutating,
  onCancel,
  onPageChange,
  onTabChange,
  onCreate,
  onDelete,
}: {
  canUpdate: boolean;
  visible: boolean;
  opportunity: OpportunityWorkbenchItem | null;
  followups: OpportunityFollowupRecord[];
  followupTotal: number;
  followupPage: number;
  followupPageSize: number;
  customizationProjects: Array<{
    projectId: string;
    projectCode?: string;
    projectName: string;
    selectionStatus: string;
    currentDemandVersionNumber?: string;
    sourceTraceSummary?: string;
    demandSummary?: string;
    traceCount: number;
  }>;
  followupTypeOptions: Array<{ value: string; label: string }>;
  followupLoading: boolean;
  customizationProjectsLoading: boolean;
  mutating: boolean;
  onCancel: () => void;
  onPageChange: (page: number) => void;
  onTabChange: (tab: "followups" | "traces") => void;
  onCreate: (payload: { followupType: string; content: string }) => Promise<void>;
  onDelete: (recordId: number) => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<"followups" | "traces">("followups");
  const [followupType, setFollowupType] = useState("communication");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (visible) {
      setActiveTab("followups");
      setFollowupType(followupTypeOptions[0]?.value ?? "communication");
      setContent("");
    }
  }, [followupTypeOptions, visible]);

  /** 切换跟进记录与需求追溯摘要；追溯数据仅在切入 traces 时由 runtime 拉取。 */
  function handleTabChange(tab: "followups" | "traces") {
    setActiveTab(tab);
    onTabChange(tab);
  }

  /** 保存新增跟进记录。 */
  async function handleSave() {
    const trimmed = content.trim();
    if (!trimmed) {
      return;
    }
    await onCreate({ followupType, content: trimmed });
    setContent("");
  }

  return (
    <Drawer
      className="crm-opportunity-workbench-drawer"
      title="跟进记录"
      width={620}
      visible={visible}
      footer={null}
      onCancel={onCancel}
    >
      <Spin
        block
        loading={activeTab === "followups" ? followupLoading : customizationProjectsLoading}
      >
        <div className="crm-opportunity-workbench-drawer-body">
          <div className="crm-opportunity-workbench-drawer-title">
            <strong>{opportunity?.opportunityName ?? "-"}</strong>
            <span>{opportunity?.orgName ?? "-"} · {opportunity?.travelMonth ?? "月份待补齐"}</span>
          </div>

          <section className="crm-opportunity-workbench-followup-create">
            <Typography.Text className="crm-opportunity-workbench-section-title">
              新增跟进记录
            </Typography.Text>
            <Select disabled={!canUpdate} value={followupType} onChange={(value) => setFollowupType(String(value))}>
              {followupTypeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            <Input.TextArea
              disabled={!canUpdate}
              autoSize={{ minRows: 4, maxRows: 6 }}
              placeholder="记录客户态度、早期需求、预算变化或方案方向"
              value={content}
              onChange={setContent}
            />
            <div className="crm-opportunity-workbench-followup-create-footer">
              <span>保存时间由系统自动记录。</span>
              <Button
                type="primary"
                icon={<Save size={15} />}
                loading={mutating}
                disabled={!canUpdate}
                onClick={() => void handleSave()}
              >
                保存跟进
              </Button>
            </div>
          </section>

          <div className="crm-opportunity-workbench-tabs" role="tablist" aria-label="跟进记录内容切换">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "followups"}
              onClick={() => handleTabChange("followups")}
            >
              跟进记录
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "traces"}
              onClick={() => handleTabChange("traces")}
            >
              需求追溯摘要
            </button>
          </div>

          {activeTab === "followups" ? (
            <div data-testid="opportunity-followup-list" className="crm-opportunity-workbench-followup-list">
              {followups.map((record) => {
                const isLatest =
                  record.content === opportunity?.latestFollowupContent &&
                  record.occurredAt === opportunity?.latestFollowupAt;
                return (
                  <article
                    className={[
                      "crm-opportunity-workbench-followup-card",
                      isLatest ? "crm-opportunity-workbench-followup-card-latest" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    data-latest-followup={isLatest ? "true" : undefined}
                    key={record.id}
                  >
                    <div>
                      <Tag color={isLatest ? "green" : "arcoblue"}>
                        {isLatest ? "最近跟进" : record.followupType}
                      </Tag>
                      <strong>{record.content}</strong>
                    </div>
                    <p>{record.recordedByUserName ?? "-"} · {record.occurredAt}</p>
                    <Popconfirm
                      title="确认删除该跟进记录吗？"
                      content="删除后列表不再展示该记录，历史数据将按逻辑删除保留。"
                      okText="删除"
                      cancelText="取消"
                      okButtonProps={{ status: "danger" }}
                      onOk={() => onDelete(record.id)}
                    >
                      <Tooltip content="删除">
                        <Button
                          size="mini"
                          shape="circle"
                          status="danger"
                          aria-label={`删除跟进记录 ${record.id}`}
                          icon={<Trash2 size={13} />}
                          disabled={!canUpdate}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </article>
                );
              })}
              <Pagination
                total={followupTotal}
                current={followupPage}
                pageSize={followupPageSize}
                size="small"
                onChange={onPageChange}
              />
            </div>
          ) : (
            <div className="crm-opportunity-workbench-trace-list">
              {customizationProjects.map((project) => (
                <article className="crm-opportunity-workbench-trace-card" key={project.projectId}>
                  <div className="crm-opportunity-workbench-trace-title">
                    <strong>{project.projectName}</strong>
                    <Tag color={project.selectionStatus === "selected" ? "green" : "gray"}>
                      {opportunitySelectionStatusLabels[project.selectionStatus] ?? project.selectionStatus}
                    </Tag>
                  </div>
                  <p>{project.projectCode ?? "-"} · {project.currentDemandVersionNumber ?? "暂无版本"} · 追溯 {project.traceCount} 条</p>
                  <div>
                    <span>追溯来源摘要</span>
                    <strong>{project.sourceTraceSummary ?? "暂无追溯摘要"}</strong>
                  </div>
                  <div>
                    <span>需求摘要</span>
                    <strong>{project.demandSummary ?? "暂无需求摘要"}</strong>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </Spin>
    </Drawer>
  );
}

/** 商机阶段历程抽屉。 */
function StageDrawer({
  visible,
  opportunity,
  board,
  stageLabelMap,
  loading,
  canManageManualStage,
  mutating,
  onCancel,
  onSaveManualStage,
}: {
  visible: boolean;
  opportunity: OpportunityWorkbenchItem | null;
  board?: OpportunityStageBoard;
  stageLabelMap: Map<string, string>;
  loading: boolean;
  canManageManualStage: boolean;
  mutating: boolean;
  onCancel: () => void;
  onSaveManualStage: (payload: { stageCode: string; note: string }) => Promise<void>;
}) {
  const [editingStageCode, setEditingStageCode] = useState<string | null>(null);
  const [manualNote, setManualNote] = useState("");
  const [expandedStageCode, setExpandedStageCode] = useState<string | null>(null);
  useEffect(() => {
    setEditingStageCode(null);
    setManualNote("");
    setExpandedStageCode(null);
  }, [board?.opportunityId, visible]);
  const manualNodes = board?.nodes.filter((node) => node.kind === "manual") ?? [];
  const autoNodes = board?.nodes.filter((node) => node.kind === "derived") ?? [];
  const dealNodes = autoNodes.filter((node) => node.sequence <= 8);
  const fulfillmentNodes = autoNodes.filter((node) => node.sequence >= 9);
  const renderNode = (node: OpportunityStageBoard["nodes"][number]) => {
    const history = node.history ?? [];
    const isCurrent = node.code === board?.currentStage;
    const isEditing = editingStageCode === node.code;
    const statusLabel = isCurrent
      ? "当前"
      : node.status === "completed"
        ? "已完成"
        : node.status === "manual_set"
          ? "已设置"
          : "未开始";
    const historyLabel = history.length
      ? expandedStageCode === node.code
        ? "收起历史"
        : `查看历史（${history.length}）`
      : "暂无历史";
    return (
      <article
        key={node.code}
        className={`crm-opportunity-workbench-stage-node is-${node.status}${
          isCurrent ? " is-current" : ""
        }${isEditing ? " is-editing" : ""}`}
        data-testid={`opportunity-stage-node-${node.code}`}
      >
        <span className="crm-opportunity-workbench-stage-node__marker">
          {node.status === "completed" ? "✓" : node.sequence}
        </span>
        <div className="crm-opportunity-workbench-stage-node__content">
          <div className="crm-opportunity-workbench-stage-node__heading">
            <button
              type="button"
              className="crm-opportunity-workbench-stage-node__summary"
              aria-expanded={expandedStageCode === node.code}
              onClick={() =>
                setExpandedStageCode((current) => (current === node.code ? null : node.code))
              }
            >
              <span className="crm-opportunity-workbench-stage-node__title">
                <strong>{node.label}</strong>
                <Tag
                  color={
                    isCurrent
                      ? "arcoblue"
                      : node.status === "completed"
                        ? "green"
                        : node.status === "manual_set"
                          ? "orange"
                          : "gray"
                  }
                >
                  {statusLabel}
                </Tag>
              </span>
              <span className="crm-opportunity-workbench-stage-node__history-toggle">{historyLabel}</span>
            </button>
            {node.kind === "manual" && canManageManualStage ? (
              <Button
                size="mini"
                type={isEditing ? "primary" : "outline"}
                aria-label={`${isEditing ? "收起" : "设置"} ${node.label}`}
                onClick={() => {
                  if (isEditing) {
                    setEditingStageCode(null);
                    setManualNote("");
                    return;
                  }
                  setEditingStageCode(node.code);
                  setManualNote(
                    node.manualNote ??
                      (board?.manualStageCode === node.code ? board.manualStageNote ?? "" : ""),
                  );
                }}
              >
                {isEditing ? "收起" : "设置"}
              </Button>
            ) : null}
          </div>
          <p>{node.manualNote ?? node.summary ?? "尚无阶段证据"}</p>
          {node.lastChangedAt ? (
            <small>
              {node.lastChangedAt} · {node.changedByUserName ?? node.triggerSource ?? "系统"}
            </small>
          ) : null}
          {isEditing ? (
            <div className="crm-opportunity-workbench-manual-stage-form">
              <Input.TextArea
                value={manualNote}
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder={`填写“${node.label}”阶段说明`}
                maxLength={1000}
                onChange={setManualNote}
              />
              <div className="crm-opportunity-workbench-manual-stage-form__actions">
                <Button
                  size="small"
                  onClick={() => {
                    setEditingStageCode(null);
                    setManualNote("");
                  }}
                >取消</Button>
                <Button
                  type="primary"
                  size="small"
                  loading={mutating}
                  disabled={!manualNote.trim()}
                  onClick={() =>
                    Modal.confirm({
                      title: "确认保存阶段设置？",
                      content: `商机将进入“${node.label}”，并记录本次说明、操作人和时间。`,
                      onOk: async () => {
                        await onSaveManualStage({
                          stageCode: node.code,
                          note: manualNote.trim(),
                        });
                        setEditingStageCode(null);
                        setManualNote("");
                      },
                    })
                  }
                >
                  保存阶段
                </Button>
              </div>
            </div>
          ) : null}
          {expandedStageCode === node.code && history.length ? (
            <div className="crm-opportunity-workbench-stage-history">
              {history.map((log) => (
                <div key={log.id}>
                  <span>{log.changedAt}</span>
                  <span>{log.reason ?? "阶段变化"}</span>
                  <span>
                    {log.changedByUserName ?? log.triggerSource}
                    {log.sourceObjectType || log.sourceObjectId
                      ? ` · ${log.sourceObjectType ?? "来源"}/${log.sourceObjectId ?? "-"}`
                      : ""}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </article>
    );
  };
  return (
    <Drawer
      className="crm-opportunity-workbench-stage-drawer"
      title="商机阶段线路图"
      width={720}
      visible={visible}
      footer={null}
      onCancel={onCancel}
    >
      <Spin loading={loading}>
        <div className="crm-opportunity-workbench-drawer-body">
          <div className="crm-opportunity-workbench-drawer-title">
            <strong>{opportunity?.opportunityName ?? board?.opportunityName ?? "-"}</strong>
            <span>{opportunity?.opportunityCode ?? board?.opportunityCode ?? "-"}</span>
          </div>
          {board?.stageStalled ? (
            <div className="crm-opportunity-workbench-stage-stalled-alert">
              阶段已连续 {board.stageStalledDays} 天没有真实变化，请负责人及时跟进（最后变化：{board.stageStalledSince ?? "暂无"}）。
            </div>
          ) : null}
          {board?.stageHistoryMissing ? (
            <div className="crm-opportunity-workbench-stage-history-missing">历史阶段日志缺失，当前时间以商机创建时间为停滞起点。</div>
          ) : null}
          <div className="crm-opportunity-workbench-stage-board-summary">
            <span>当前阶段：{board?.currentStageLabel ?? stageLabelMap.get(opportunity?.stage ?? "") ?? "阶段待设置"}</span>
            <span>
              来源：
              {board?.stageSource === "manual"
                ? "人工"
                : board?.stageSource === "derived"
                  ? "业务事实"
                  : "待确认"}
            </span>
          </div>
          <section className="crm-opportunity-workbench-stage-section">
            <header>
              <h4>前期人工阶段</h4>
              <span>负责人可设置</span>
            </header>
            <div className="crm-opportunity-workbench-stage-timeline">
              {manualNodes.map(renderNode)}
            </div>
          </section>
          <section className="crm-opportunity-workbench-stage-section">
            <header>
              <h4>定制与成交阶段</h4>
              <span>业务事实自动推进</span>
            </header>
            <div className="crm-opportunity-workbench-stage-timeline">{dealNodes.map(renderNode)}</div>
          </section>
          <section className="crm-opportunity-workbench-stage-section">
            <header>
              <h4>履约与财务阶段</h4>
              <span>业务事实自动推进</span>
            </header>
            <div className="crm-opportunity-workbench-stage-timeline">
              {fulfillmentNodes.map(renderNode)}
            </div>
          </section>
        </div>
      </Spin>
    </Drawer>
  );
}

/** 商机阶段负责人设置抽屉。 */
function StageOwnerDrawer({
  canUpdate,
  visible,
  opportunity,
  owners,
  stageGroupOptions,
  staffOptions,
  stageGroupLabelMap,
  loading,
  mutating,
  onCancel,
  onSave,
}: {
  canUpdate: boolean;
  visible: boolean;
  opportunity: OpportunityWorkbenchItem | null;
  owners: OpportunityStageOwnerItem[];
  stageGroupOptions: Array<{ value: string; label: string }>;
  staffOptions: Array<{ id: number; displayName: string; username: string; disabled: boolean }>;
  stageGroupLabelMap: Map<string, string>;
  loading: boolean;
  mutating: boolean;
  onCancel: () => void;
  onSave: (payload: { assignments: Record<string, number | undefined>; remark?: string }) => Promise<void>;
}) {
  const [assignments, setAssignments] = useState<Record<string, number | undefined>>({});

  useEffect(() => {
    if (!visible) {
      return;
    }
    setAssignments(
      Object.fromEntries(owners.map((owner) => [owner.stageGroup, owner.ownerUserId])),
    );
  }, [owners, visible]);

  const latestOwner = owners[0];
  const latestSummary = latestOwner
    ? `${stageGroupLabelMap.get(latestOwner.stageGroup) ?? latestOwner.stageGroup} -> ${latestOwner.ownerUserName ?? "-"}`
    : "暂无变更";

  return (
    <Drawer
      className="crm-opportunity-workbench-drawer"
      title="阶段负责人设置"
      width={600}
      visible={visible}
      onCancel={onCancel}
      footer={
        <div className="crm-opportunity-workbench-drawer-footer">
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            icon={<Save size={15} />}
            loading={mutating}
            disabled={!canUpdate}
            onClick={() => void onSave({ assignments, remark: "商机工作台统一保存阶段负责人" })}
          >
            保存设置
          </Button>
        </div>
      }
    >
      <Spin loading={loading}>
        <div className="crm-opportunity-workbench-drawer-body">
          <div className="crm-opportunity-workbench-owner-summary">
            <span>主负责人：{opportunity?.ownerUserName ?? "-"}</span>
            <span>创建人：{opportunity?.createdByUserName ?? "-"}</span>
            <span>区域负责人：待区域命中</span>
          </div>
          <div className="crm-opportunity-workbench-quick-actions">
            <Button size="small" onClick={() => bulkAssignFrom("customization")}>
              从定制及以后全部设置
            </Button>
            <Button size="small" onClick={() => bulkAssignFrom("contract")}>
              从合同及以后全部设置
            </Button>
            <Button size="small" onClick={() => setAssignments({})}>
              清空后续负责人
            </Button>
          </div>
          <div className="crm-opportunity-workbench-stage-owner-grid">
            {stageGroupOptions.map((group) => (
              <label key={group.value}>
                <span>{group.label}</span>
                <Select
                  allowClear
                  showSearch
                  placeholder="选择负责人"
                  value={assignments[group.value]}
                  filterOption={(inputValue, option) =>
                    String(
                      (option as ReactElement<{ children?: ReactNode }>).props.children ?? "",
                    )
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={(value) =>
                    setAssignments((current) => ({
                      ...current,
                      [group.value]: value ? Number(value) : undefined,
                    }))
                  }
                >
                  {staffOptions.map((staff) => (
                    <Select.Option key={staff.id} value={staff.id} disabled={staff.disabled}>
                      {staff.displayName || staff.username}
                    </Select.Option>
                  ))}
                </Select>
              </label>
            ))}
          </div>
          <div className="crm-opportunity-workbench-latest-change">
            最近变更：{latestSummary}
          </div>
        </div>
      </Spin>
    </Drawer>
  );

  /** 从指定阶段开始批量设置为同一负责人。 */
  function bulkAssignFrom(startGroup: string) {
    const currentOwner = assignments[startGroup] ?? owners.find((owner) => owner.ownerUserId)?.ownerUserId;
    if (!currentOwner) {
      return;
    }
    const startIndex = stageGroupOptions.findIndex((group) => group.value === startGroup);
    setAssignments((current) => {
      const next = { ...current };
      stageGroupOptions.slice(Math.max(startIndex, 0)).forEach((group) => {
        next[group.value] = currentOwner;
      });
      return next;
    });
  }
}

/** 把后端选项转为快速取中文标签的 Map。 */
function buildLabelMap(options?: Array<{ value: string; label: string }>) {
  return new Map((options ?? []).map((option) => [option.value, option.label]));
}

/** 截断表格中的长文本，完整内容通过 Tooltip 展示。 */
function truncateText(value: string, maxLength: number) {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}

/** 组装最近行程悬停摘要：定制名、行程标题、目的地等。 */
function buildLatestItineraryTooltip(item: OpportunityWorkbenchItem) {
  const rows = [
    ["定制号", item.latestItineraryProjectCode],
    ["定制/方案", item.latestItineraryProjectName],
    ["需求版本", item.latestItineraryDemandVersionNumber],
    ["行程标题", item.latestItineraryTitle],
    ["目的地", item.latestItineraryDestinationSummary],
    ["最近更新", item.latestItineraryUpdatedAt],
  ].filter(([, value]) => Boolean(String(value ?? "").trim())) as Array<[string, string]>;
  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="crm-opportunity-workbench-latest-itinerary-tooltip">
      {rows.map(([label, value]) => (
        <div className="crm-opportunity-workbench-latest-itinerary-tooltip__row" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

/** 格式化学校候选项；直辖市省市同名时只展示一次。 */
function formatOrganizationOption(option: OpportunityOrganizationOption) {
  const areaParts = [option.province, option.city]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));
  const area = Array.from(new Set(areaParts)).join(" ");
  return area ? `${option.orgName} / ${area}` : option.orgName;
}

/** 生成新增商机时的系统建议名称。 */
function buildSuggestedOpportunityName(draft: OpportunityEditorDraft) {
  const schoolName = normalizeSchoolName(draft.orgName);
  const customerName = normalizeCustomerName(draft.customerName, schoolName);
  const contactName = formatContactNameForOpportunityName(
    draft.contactName,
    draft.contactPositionName,
  );
  const travelText = formatTravelSummary(draft.travelMonth, draft.travelerCount);
  const destinationText = formatStudyDestinationSummary(
    draft.studyDestination,
    draft.studyDayCount,
  );
  const customerText = customerName ? `【客户：${customerName}】` : "";
  const detailText = [contactName, travelText, customerText, destinationText]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join("");
  return [schoolName, detailText].filter(Boolean).join(" ");
}

/** 生成商机名称中的联系人片段。 */
function formatContactNameForOpportunityName(contactName?: string, positionName?: string) {
  if (!contactName?.trim()) {
    return "";
  }
  const normalizedContactName = contactName.trim();
  const normalizedPositionName = positionName?.trim();
  return normalizedPositionName
    ? `${normalizedPositionName}-${normalizedContactName}`
    : normalizedContactName;
}

/** 生成商机名称中的出行月份和人数片段。 */
function formatTravelSummary(travelMonth?: string, travelerCount?: number) {
  const monthText = formatTravelMonthForOpportunityName(travelMonth);
  const travelerText = travelerCount ? `${travelerCount}人出行` : "";
  return `${monthText}${travelerText}`.trim();
}

/** 将 YYYY-MM 出行月份转换为商机名称中的月份。 */
function formatTravelMonthForOpportunityName(travelMonth?: string) {
  const match = travelMonth?.match(/^\d{4}-(\d{2})$/);
  return match ? `${Number(match[1])}月` : "";
}

/** 生成商机名称中的考察目的地和课程学习天数片段。 */
function formatStudyDestinationSummary(destination?: string, dayCount?: number) {
  const normalizedDestination = destination?.trim() ?? "";
  const dayText = dayCount ? `${dayCount}日` : "";
  return `${normalizedDestination}${dayText}`.trim();
}

/** 学校名去掉省市前缀，保留实际学校名称。 */
function normalizeSchoolName(orgName?: string) {
  const normalized = orgName?.trim();
  if (!normalized) {
    return "";
  }
  return normalized
    .replace(
      /^(北京市|天津市|上海市|重庆市|[\u4e00-\u9fa5]{2,7}(?:省|自治区|特别行政区)|[\u4e00-\u9fa5]{2,7}市)+/,
      "",
    )
    .trim() || normalized;
}

/** 客户名中若重复带学校名，则压缩成更短的客户方向。 */
function normalizeCustomerName(customerName?: string, schoolName?: string) {
  const normalized = customerName?.trim();
  if (!normalized) {
    return "";
  }
  if (schoolName && normalized.startsWith(schoolName)) {
    return normalized.slice(schoolName.length).trim() || normalized;
  }
  return normalized;
}

/** 格式化联系人候选项。 */
function formatContactOption(option: OpportunityContactOption) {
  return [option.contactName, option.positionName, option.mobile].filter(Boolean).join(" / ");
}

/** 长候选项标签，避免客户名、学校名和联系人信息被下拉框截断。 */
function OptionLabel({ title }: { title: string }) {
  return (
    <span className="crm-opportunity-workbench-option-label" title={title}>
      {title}
    </span>
  );
}

/** 归一化月份输入，保留 YYYY-MM。 */
function normalizeTravelMonthInput(value?: string) {
  const normalized = value?.trim();
  return normalized && /^\d{4}-\d{2}$/.test(normalized) ? normalized : undefined;
}

/** 把 Select 值归一化为可选数字。 */
function parseOptionalSelectNumber(value: unknown) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** 把 InputNumber 值归一化为可选正整数。 */
function parseOptionalInputNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : undefined;
}
